import React, { useEffect, useState } from "react";
import { REGEX, validateFields } from "@/lib/validators";
import { useNavigate, useParams } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Minus, Edit, ArrowLeft, Package, TrendingUp, ShoppingCart, Warehouse, ScrollText, Users, ChevronRight, Truck, CheckCircle2, ClipboardList, Trash2, IndianRupee, Percent, Handshake, IdCard, Paperclip, Boxes, History, Receipt, RotateCcw } from "lucide-react";
import LocationDocumentsBlock from "./LocationDocumentsBlock";
import { ReturnRequestButton } from "./ReturnComponents";
import { useAuth } from "@/context/AuthContext";
import { ResetLocationButton } from "./DayPlanPages";

// ============================================================================
// Shared: PageHeader
// ============================================================================
export function PageHeader({ title, subtitle, action, back }) {
  const nav = useNavigate();
  return (
    <div className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-start gap-3 min-w-0">
        {back && (
          <button onClick={() => nav(back)} className="mt-1 p-1.5 rounded-lg hover:bg-slate-100 shrink-0" data-testid="back-btn">
            <ArrowLeft size={18} className="text-slate-600" />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 truncate">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function EmptyState({ icon: Icon = Package, title, description, action }) {
  return (
    <Card className="p-12 text-center border-dashed">
      <div className="mx-auto h-12 w-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-4">
        <Icon size={22} />
      </div>
      <div className="font-semibold text-slate-900">{title}</div>
      {description && <div className="text-sm text-slate-500 mt-1">{description}</div>}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  );
}

// Read-only documents + location gallery (KYC preview for distributor/retailer)
export function DocumentsGallery({ documents = [], locationLink, lat, lng }) {
  const mapsLink = locationLink || (lat && lng ? `https://maps.google.com/?q=${lat},${lng}` : "");
  return (
    <div className="rounded-xl border border-[#c9a227]/30 bg-[#faf6e6]/40 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-slate-900 text-sm">KYC Documents</div>
        {mapsLink && (
          <a href={mapsLink} target="_blank" rel="noreferrer" className="text-xs text-[#8a6600] underline">View location on Map</a>
        )}
      </div>
      {(!documents || documents.length === 0) ? (
        <div className="text-xs text-slate-500">No documents uploaded.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {documents.map((d, i) => {
            const isImg = (d.type || "").startsWith("image/") || /^data:image\//.test(d.url || "");
            return (
              <a key={i} href={d.url} target="_blank" rel="noreferrer"
                 className="block rounded-lg border border-slate-200 bg-white overflow-hidden hover:shadow-md transition" title={d.name}>
                {isImg && d.url ? (
                  <img src={d.url} alt={d.name} className="h-28 w-full object-cover" />
                ) : (
                  <div className="h-28 w-full flex items-center justify-center bg-slate-50 text-slate-400">
                    <Paperclip size={22} />
                  </div>
                )}
                <div className="px-2 py-1.5 text-[11px] text-slate-700 truncate border-t border-slate-100">{d.name || `Document ${i + 1}`}</div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Clickable Finance stat cell — used on the dashboard Cash & Bank Snapshot
function FinanceStat({ label, value, valueClass = "", borderLeft = false, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick?.(); }}
      className={`group rounded-lg cursor-pointer p-2 -m-2 hover:bg-amber-50/60 transition ${borderLeft ? "border-l pl-4" : ""}`}
      data-testid={`finance-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
        {label}
        <ChevronRight size={11} className="text-slate-300 group-hover:text-[#a67c00] transition-colors" />
      </div>
      <div className={`text-xl font-bold ${valueClass} group-hover:underline decoration-dotted underline-offset-4`}>
        {value}
      </div>
    </div>
  );
}

// ============================================================================
// Owner — Dashboard
// ============================================================================
export function OwnerDashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [recent, setRecent] = useState([]);
  const [finance, setFinance] = useState(null);
  const [boxStats, setBoxStats] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    dms.ownerDashboard().then(d => setKpis(d.kpis)).catch(() => {});
    dms.listOrders().then(d => setRecent((d.data || []).slice(0, 5))).catch(() => {});
    dms.financeSnapshot().then(setFinance).catch(() => {});
    dms.cpnBoxStats().then(setBoxStats).catch(() => {});
  }, []);
  const cards = [
    { label: "Distributors",         value: kpis?.distributors ?? "—",           icon: Handshake,    color: "teal",    to: "/dms/owner/distributors" },
    { label: "Products",             value: kpis?.products ?? "—",               icon: Package,      color: "indigo",  to: "/dms/owner/products" },
    { label: "Pending Orders",       value: kpis?.pending_orders ?? "—",         icon: ShoppingCart, color: "amber",   to: "/dms/owner/primary-orders?status=pending" },
    { label: "Ready to Dispatch",    value: kpis?.ready_to_go ?? "—",            icon: Truck,        color: "blue",    to: "/dms/owner/primary-orders?status=ready_to_go" },
    { label: "Revenue (MTD)",        value: kpis ? inr(kpis.revenue_mtd) : "—",  icon: TrendingUp,   color: "emerald", to: "/dms/owner/primary-orders" },
    { label: "Outstanding",          value: kpis ? inr(kpis.outstanding_receivable) : "—", icon: ScrollText, color: "rose",   to: "/dms/owner/ledger" },
    { label: "Inventory Value",      value: kpis ? inr(kpis.inventory_value) : "—", icon: Warehouse, color: "slate",   to: "/dms/owner/inventory" },
  ];
  const colorMap = {
    teal: "bg-[#faf6e6] text-[#a67c00]", indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700", blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-100 text-slate-700",
  };
  return (
    <div>
      <PageHeader title="Owner Dashboard" subtitle="Overview of your distribution business" />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.filter(c => !["Revenue (MTD)", "Outstanding", "Inventory Value"].includes(c.label)).map(c => (
          <Card key={c.label} className="p-4 cursor-pointer group hover:shadow-md hover:border-amber-300 hover:-translate-y-0.5 transition-all relative"
                onClick={() => nav(c.to)} data-testid={`owner-kpi-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex items-start justify-between">
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${colorMap[c.color]}`}><c.icon size={18} /></div>
              <ChevronRight size={16} className="text-slate-300 group-hover:text-[#a67c00] group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="mt-3 text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
            <div className="mt-1 text-xl font-bold text-slate-900 group-hover:text-[#8a6600] transition-colors">{c.value}</div>
            <div className="mt-2 text-[10px] text-slate-400 group-hover:text-[#a67c00] uppercase tracking-wider font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
              View details →
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Cash &amp; Bank Snapshot</h3>
          <button onClick={() => nav("/dms/finance/bank-accounts")} className="text-sm text-[#a67c00] hover:underline">Manage →</button>
        </div>
        <Card className="p-5 border-amber-200" data-testid="finance-snapshot-card">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <FinanceStat label="Cash in Bank" value={finance ? inr(finance.cash_in_bank) : "—"}
                          valueClass="text-emerald-700"
                          onClick={() => nav("/dms/finance/bank-accounts")} />
            <FinanceStat label="Cash in Hand" value={finance ? inr(finance.cash_in_hand) : "—"}
                          valueClass="text-amber-700"
                          onClick={() => nav("/dms/finance/cash-transactions")} />
            <FinanceStat label="Outstanding Loans" value={finance ? inr(finance.outstanding_loans) : "—"}
                          valueClass="text-rose-700"
                          onClick={() => nav("/dms/finance/loans")} />
            <FinanceStat label="Net Liquid" value={finance ? inr(finance.net_liquid) : "—"}
                          valueClass="text-slate-900" borderLeft
                          onClick={() => nav("/dms/finance/bank-accounts")} />
            <FinanceStat label="Net Position"
                          value={finance ? inr(finance.net_position) : "—"}
                          valueClass={finance && finance.net_position < 0 ? "text-rose-700" : "text-emerald-700"}
                          onClick={() => nav("/dms/owner/ledger")} />
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Coupon Boxes</h3>
          <button onClick={() => nav("/dms/owner/boxes")} className="text-sm text-[#a67c00] hover:underline">Manage →</button>
        </div>
        <Card className="p-5 border-amber-200 cursor-pointer hover:shadow-md transition-all"
              onClick={() => nav("/dms/owner/boxes")} data-testid="box-summary-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Boxes Created</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{boxStats ? boxStats.boxes_total : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Assigned to Distributor</div>
              <div className="mt-1 text-2xl font-bold text-emerald-700">{boxStats ? boxStats.boxes_assigned : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Coupons in Boxes</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{boxStats ? boxStats.coupons_in_boxes : "—"}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">Coupons Claimed</div>
              <div className="mt-1 text-2xl font-bold text-[#a67c00]">{boxStats ? boxStats.coupons_claimed : "—"}</div>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Recent Primary Orders</h3>
          <button onClick={() => nav("/dms/owner/primary-orders")} className="text-sm text-[#a67c00] hover:underline">View all →</button>
        </div>
        <Card className="overflow-hidden">
          {recent.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No orders yet</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order #</TableHead>
                  <TableHead>Distributor</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Fulfillment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Placed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map(o => (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/dms/owner/primary-orders/${o.id}`)}>
                    <TableCell className="font-mono text-sm">{o.order_no}</TableCell>
                    <TableCell>{o.distributor_name}</TableCell>
                    <TableCell className="font-medium">{inr(o.total)}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0" style={{ width: `${o.fulfillment_pct}%` }} /></div><span className="text-xs text-slate-600">{o.fulfillment_pct}%</span></div></TableCell>
                    <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPill(o.status)}`}>{o.status.replace(/_/g, " ")}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
// Owner — Categories
// ============================================================================
export function CategoriesPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  const load = () => dms.listCategories().then(d => setList(d.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) { await dms.updateCategory(editing.id, form); toast.success("Updated"); }
      else { await dms.createCategory(form); toast.success("Created"); }
      setOpen(false); setEditing(null); setForm({ name: "", description: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await dms.deleteCategory(c.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Product Categories"
        subtitle="Group your products (Engine Oil, Gear Oil, Brake Fluid, etc.)"
        action={<Button onClick={() => { setEditing(null); setForm({ name: "", description: "" }); setOpen(true); }} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="add-category-btn"><Plus size={16} className="mr-1" /> New Category</Button>}
      />
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Description</TableHead><TableHead>Created</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map(c => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-slate-600 text-sm">{c.description || "—"}</TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(c.created_at)}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => { setEditing(c); setForm({ name: c.name, description: c.description || "" }); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-cat-${c.id}`}><Edit size={14} /></button>
                  <button onClick={() => del(c)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded ml-1"><Trash2 size={14} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No categories yet</div>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="cat-name-input" /></div>
            <div><Label>Description (optional)</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-cat-btn">{editing ? "Update" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Owner — Invoice Numbering (Vyapar-style editable prefix + running number)
// ============================================================================
export function InvoiceNumberingPage() {
  const [settings, setSettings] = useState(null);
  const [addingFor, setAddingFor] = useState(null);
  const [newPrefix, setNewPrefix] = useState("");
  const [nextSeqDraft, setNextSeqDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => dms.getNumberSettings().then(d => {
    setSettings(d);
    setNextSeqDraft({ ebill: d.ebill?.next_seq ?? 1, retailer_bill: d.retailer_bill?.next_seq ?? 1 });
  });
  useEffect(() => { load(); }, []);

  const setPrefix = async (docType, prefix) => {
    setBusy(true);
    try { await dms.updateNumberSettings(docType, { prefix }); toast.success("Prefix updated"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const addPrefix = async (docType) => {
    const p = newPrefix.trim();
    if (!p) return;
    await setPrefix(docType, p);
    setNewPrefix(""); setAddingFor(null);
  };

  const saveNextSeq = async (docType) => {
    setBusy(true);
    try { await dms.updateNumberSettings(docType, { next_seq: Number(nextSeqDraft[docType]) }); toast.success("Next number updated"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  if (!settings) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const sections = [
    { key: "ebill", title: "e-Bill (Dealer / Distributor Bill)" },
    { key: "retailer_bill", title: "Retailer Bill (Invoice)" },
  ];

  return (
    <div>
      <PageHeader title="Invoice Numbering" subtitle="Set a custom prefix and the next number for each bill type" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sections.map(sec => {
          const s = settings[sec.key] || {};
          const known = s.known_prefixes || [];
          const width = s.width || 4;
          const seqPreview = String(s.next_seq ?? 1).padStart(width, "0");
          const preview = s.prefix ? `${s.prefix}-${seqPreview}` : seqPreview;
          return (
            <Card key={sec.key} className="p-4">
              <div className="font-semibold mb-3">{sec.title}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Prefix</div>
              <div className="flex flex-wrap gap-2 mb-4">
                <button onClick={() => setPrefix(sec.key, "")} disabled={busy}
                  className={`px-3 py-1.5 rounded-full text-sm border ${!s.prefix ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>None</button>
                {known.map(p => (
                  <button key={p} onClick={() => setPrefix(sec.key, p)} disabled={busy}
                    className={`px-3 py-1.5 rounded-full text-sm border ${s.prefix === p ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>{p}</button>
                ))}
                {addingFor === sec.key ? (
                  <div className="flex items-center gap-1">
                    <Input value={newPrefix} onChange={e => setNewPrefix(e.target.value)} placeholder="e.g. MAR" className="w-24 h-8 text-sm" autoFocus />
                    <Button size="sm" onClick={() => addPrefix(sec.key)} disabled={busy}>Add</Button>
                  </div>
                ) : (
                  <button onClick={() => { setAddingFor(sec.key); setNewPrefix(""); }} className="px-3 py-1.5 rounded-full text-sm border border-dashed border-slate-300 text-[#a67c00]">+ Add Prefix</button>
                )}
              </div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Next Number</div>
              <div className="flex items-center gap-2 mb-3">
                <Input type="number" min={1} value={nextSeqDraft[sec.key] ?? ""} onChange={e => setNextSeqDraft({ ...nextSeqDraft, [sec.key]: e.target.value })} className="w-32" />
                <Button size="sm" variant="outline" onClick={() => saveNextSeq(sec.key)} disabled={busy}>Save</Button>
              </div>
              <div className="text-sm text-slate-600">Next number will be: <span className="font-mono font-semibold text-slate-900">{preview}</span></div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Owner — Products
// ============================================================================
// ============================================================================
// Owner — UNIFIED Product Master (products + categories + current pricing in ONE page)
// ============================================================================
export function ProductsPage() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeLines, setActiveLines] = useState({});     // product_id -> latest active circular line
  const [circulars, setCirculars] = useState([]);         // batch history
  const [loading, setLoading] = useState(true);

  const [prodOpen, setProdOpen] = useState(false);
  const [editingProd, setEditingProd] = useState(null);
  const [form, setForm] = useState({ material_description: "", grade_specs: "", pack_size: "", category_id: "", sku_code: "" });

  const [catsOpen, setCatsOpen] = useState(false);        // manage-categories modal
  const [histOpen, setHistOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);        // price history / batch modal

  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes, ccRes] = await Promise.all([
        dms.listProducts(),
        dms.listCategories(),
        dms.listPriceCirculars(),
      ]);
      setList(pRes.data || []);
      setCats(cRes.data || []);
      const cs = ccRes.data || [];
      setCirculars(cs);
      // Fetch the LATEST active line for each product by pulling the current top circular's details
      // Build a merged map: for each product, find the newest active line across all circulars
      const map = {};
      // Fetch details of the top 5 circulars (batches are usually recent-heavy)
      const details = await Promise.all(cs.slice(0, 5).map(c => dms.getPriceCircular(c.id)));
      details.forEach(det => {
        (det.lines || []).forEach(l => {
          if (!l.is_active) return;
          const prev = map[l.product_id];
          if (!prev || (l.batch_no || 0) > (prev.batch_no || 0)) map[l.product_id] = l;
        });
      });
      setActiveLines(map);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNewProduct = () => {
    if (cats.length === 0) { toast.error("Create a category first"); return; }
    setEditingProd(null);
    setForm({ material_description: "", grade_specs: "-", pack_size: "", category_id: cats[0].id, sku_code: "" });
    setProdOpen(true);
  };
  const openEditProduct = (p) => {
    setEditingProd(p);
    setForm({
      material_description: p.material_description || p.name || "",
      grade_specs: p.grade_specs || "-",
      pack_size: p.pack_size || "",
      category_id: p.category_id,
      sku_code: p.sku_code,
    });
    setProdOpen(true);
  };
  const saveProduct = async () => {
    try {
      const body = {
        name: `${form.material_description} (${form.pack_size})`,
        material_description: form.material_description,
        grade_specs: form.grade_specs,
        pack_size: form.pack_size,
        category_id: form.category_id,
        sku_code: form.sku_code,
        box_qty: editingProd?.box_qty ?? 1,
        unit_price: editingProd?.unit_price ?? 0,
      };
      if (editingProd) { await dms.updateProduct(editingProd.id, body); toast.success("Product updated"); }
      else { await dms.createProduct(body); toast.success("Product created — set pricing via Update Prices"); }
      setProdOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = list.filter(p => {
    if (catFilter !== "all" && p.category_id !== catFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      const hay = `${p.material_description || p.name} ${p.grade_specs} ${p.pack_size} ${p.category_name}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  // group by category, PDF-style
  const grouped = {};
  filtered.forEach(p => {
    const k = p.category_name || "Uncategorised";
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(p);
  });
  const catOrder = Object.keys(grouped).sort();

  const activeCircular = circulars.find(c => c.is_active) || circulars[0];

  return (
    <div>
      <PageHeader
        title="Product Master"
        subtitle={`${list.length} products · ${cats.length} categories · ${activeCircular ? `Current pricing from ${activeCircular.batch_label}` : "No pricing set"}`}
        action={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setCatsOpen(true)} className="border-slate-300" data-testid="manage-categories-btn">
              <Boxes size={15} className="mr-1.5" /> Manage Categories
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="delete-all-products-btn"
              onClick={async () => {
                if (list.length === 0) { toast.error("No products to delete"); return; }
                const ok = window.confirm(
                  `Delete ALL ${list.length} products permanently?\n\nThis cannot be undone. Categories will be kept so you can re-import fresh data.`
                );
                if (!ok) return;
                try {
                  const res = await dms.deleteAllProducts();
                  toast.success(`Deleted ${res.deleted ?? list.length} products`);
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to delete products");
                }
              }}
            >
              <Trash2 size={15} className="mr-1.5" /> Delete All Products
            </Button>
            <Button variant="outline" onClick={() => setHistOpen(true)} className="border-slate-300" data-testid="price-history-btn">
              <History size={15} className="mr-1.5" /> Price History
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-slate-300" data-testid="bulk-margin-btn">
              <Edit size={15} className="mr-1.5" /> Bulk Edit Margin
            </Button>
            <ExcelButtons onImported={load} />
            <Button onClick={openNewProduct} disabled={cats.length === 0} variant="outline" className="border-[#c9a227] text-[#8a6600] hover:bg-[#faf6e6]" data-testid="add-product-btn">
              <Plus size={15} className="mr-1.5" /> New Product
            </Button>
            <Button onClick={() => nav("/dms/owner/price-circulars/new")} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white shadow-sm" data-testid="update-prices-btn">
              <IndianRupee size={15} className="mr-1.5" /> Update Prices
            </Button>
          </div>
        }
      />

      {cats.length === 0 && (
        <Card className="p-4 mb-4 bg-[#faf6e6] border-[#c9a227]/40 text-[#8a6600] text-sm flex items-center justify-between">
          <span>No categories yet. Click <b>Manage Categories</b> to add your first category, then add products.</span>
          <Button size="sm" onClick={() => setCatsOpen(true)} className="bg-[#c9a227] hover:bg-[#a67c00] text-white">Manage Categories</Button>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[220px]"><Input placeholder="Search Material / Grade / Pack…" value={q} onChange={e => setQ(e.target.value)} data-testid="prod-search-input" /></div>
        <div className="w-64">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="prod-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories ({cats.length})</SelectItem>
              {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}

      {!loading && catOrder.length === 0 && list.length > 0 && (
        <div className="p-8 text-center text-sm text-slate-500">No products match your filter</div>
      )}

      {!loading && catOrder.map((catName) => {
        const rows = grouped[catName];
        return (
          <Card key={catName} className="mb-4 overflow-hidden border-[#c9a227]/15 shadow-sm">
            <div className="px-4 py-2.5 bg-gradient-to-r from-[#faf6e6] to-white border-b border-[#c9a227]/20 flex items-center justify-between">
              <div className="font-display font-bold text-[#8a6600] text-sm uppercase tracking-wide">{catName}</div>
              <div className="text-xs text-slate-500">{rows.length} SKU{rows.length !== 1 ? "s" : ""}</div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60">
                    <TableHead className="min-w-[220px]">Material Description</TableHead>
                    <TableHead>Grade / Specs</TableHead>
                    <TableHead>Pack Size</TableHead>
                    <TableHead className="text-right">MRP</TableHead>
                    <TableHead className="text-right">Price w/GST</TableHead>
                    <TableHead className="text-right">Price w/o GST</TableHead>
                    <TableHead>GST %</TableHead>
                    <TableHead className="text-right">Pcs/Box</TableHead>
                    <TableHead className="text-right">Price/Box</TableHead>
                    <TableHead className="text-right">Ltr/Box</TableHead>
                    <TableHead className="text-right">DLP (current)</TableHead>
                    <TableHead>Margin %</TableHead>
                    <TableHead>Cash Coupon</TableHead>
                    <TableHead>FOC</TableHead>
                    <TableHead>Monthly Gift</TableHead>
                    <TableHead>Trade Disc.</TableHead>
                    <TableHead className="w-12 text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(p => {
                    const line = activeLines[p.id];
                    const dlp = line?.dlp ?? p.unit_price;
                    const prev = p.previous_price;
                    return (
                      <TableRow key={p.id} className="hover:bg-[#faf6e6]/40">
                        <TableCell className="font-medium text-slate-900">{p.material_description || p.name}</TableCell>
                        <TableCell><span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-mono">{p.grade_specs || "-"}</span></TableCell>
                        <TableCell className="text-slate-700 font-medium">{p.pack_size || "-"}</TableCell>
                        <TableCell className="text-right text-slate-700">{line?.mrp ? inr(line.mrp) : "—"}</TableCell>
                        <TableCell className="text-right text-slate-700">{p.price_with_gst ? inr(p.price_with_gst) : "—"}</TableCell>
                        <TableCell className="text-right text-slate-700">{p.price_without_gst ? inr(p.price_without_gst) : "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600">{p.gst_pct ? `${p.gst_pct}%` : "—"}</TableCell>
                        <TableCell className="text-right text-slate-700">{p.piece_per_box || "—"}</TableCell>
                        <TableCell className="text-right text-slate-700">{p.price_per_box ? inr(p.price_per_box) : "—"}</TableCell>
                        <TableCell className="text-right text-slate-700">{p.liter_per_box || "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="font-bold text-[#8a6600]">{dlp ? inr(dlp) : "—"}</div>
                          {prev && prev !== dlp && (
                            <div className="text-[10px] text-slate-400 line-through">{inr(prev)}</div>
                          )}
                        </TableCell>
                        <TableCell>{line?.distributor_margin_pct ? <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-semibold">{line.distributor_margin_pct}%</span> : "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-[110px] truncate" title={line?.cash_coupon}>{line?.cash_coupon || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600">{line?.foc_benefits || "—"}</TableCell>
                        <TableCell className="text-xs text-slate-600">{line?.monthly_gift || "—"}</TableCell>
                        <TableCell className="text-xs">{line?.trade_discount ? <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-semibold">{line.trade_discount}</span> : "—"}</TableCell>
                        <TableCell className="text-right">
                          <button onClick={() => openEditProduct(p)} className="p-1.5 hover:bg-[#faf6e6] rounded" data-testid={`edit-prod-${p.id}`} title="Edit product info"><Edit size={14} className="text-[#a67c00]" /></button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        );
      })}

      {/* ── Product create/edit modal ── */}
      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingProd ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Material Description *</Label><Input value={form.material_description} onChange={e => setForm({ ...form, material_description: e.target.value })} data-testid="prod-name-input" placeholder="e.g. POWER 4T 20W40" /></div>
            <div><Label>Grade / Specs</Label><Input value={form.grade_specs} onChange={e => setForm({ ...form, grade_specs: e.target.value })} placeholder="e.g. SN, GL5" /></div>
            <div><Label>Pack Size *</Label><Input value={form.pack_size} onChange={e => setForm({ ...form, pack_size: e.target.value })} placeholder="e.g. 1 L / 5 kg" /></div>
            <div><Label>Category *</Label>
              <Select value={form.category_id} onValueChange={v => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="prod-cat-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>SKU Code *</Label><Input value={form.sku_code} onChange={e => setForm({ ...form, sku_code: e.target.value })} disabled={!!editingProd} data-testid="prod-sku-input" /></div>
            <div className="col-span-2 text-xs bg-[#faf6e6] border border-[#c9a227]/30 text-[#8a6600] rounded-lg p-3 mt-2">
              💡 To set MRP / DLP / margin / coupons for this product, click <b>Update Prices</b> on the main page — it publishes a new pricing batch and preserves history.
            </div>
          </div>
          <DialogFooter><Button onClick={saveProduct} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-prod-btn">{editingProd ? "Update" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Manage categories modal ── */}
      <ManageCategoriesModal open={catsOpen} onOpenChange={setCatsOpen} onChanged={load} />

      {/* ── Price history / batches modal ── */}
      <PriceHistoryModal open={histOpen} onOpenChange={setHistOpen} circulars={circulars} />
      <BulkMarginModal open={bulkOpen} onOpenChange={setBulkOpen} cats={cats} onDone={load} />
    </div>
  );
}

// ── Inline: Categories management modal ──
function ManageCategoriesModal({ open, onOpenChange, onChanged }) {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => dms.listCategories().then(d => setList(d.data || []));
  useEffect(() => { if (open) load(); }, [open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing) { await dms.updateCategory(editing.id, { name, description: desc }); toast.success("Updated"); }
      else { await dms.createCategory({ name, description: desc }); toast.success("Created"); }
      setName(""); setDesc(""); setEditing(null); load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Products in this category will be unassigned.`)) return;
    try { await dms.deleteCategory(c.id); toast.success("Deleted"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Manage Categories</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2 mb-3 items-end">
          <div className="col-span-1"><Label>Category Name</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. MCO — Synthetic Blend" data-testid="cat-name-input" /></div>
          <div className="col-span-1"><Label>Description</Label><Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="optional" /></div>
          <div><Button onClick={submit} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white w-full" data-testid="save-cat-btn">{editing ? "Update" : "Add"}</Button></div>
        </div>
        {editing && <div className="text-xs text-slate-500 mb-2">Editing &quot;{editing.name}&quot; — <button className="text-[#a67c00] underline" onClick={() => { setEditing(null); setName(""); setDesc(""); }}>cancel</button></div>}
        <Card className="max-h-[420px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Description</TableHead><TableHead className="w-24 text-right"></TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map(c => (
                <TableRow key={c.id} className="hover:bg-slate-50">
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-slate-600 text-sm">{c.description || "—"}</TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => { setEditing(c); setName(c.name); setDesc(c.description || ""); }} className="p-1.5 hover:bg-[#faf6e6] rounded" data-testid={`edit-cat-${c.id}`}><Edit size={14} className="text-[#a67c00]" /></button>
                    <button onClick={() => del(c)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded ml-1"><Trash2 size={14} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No categories yet</div>}
        </Card>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline: Bulk margin edit modal (preview -> apply -> undo) ──
function BulkMarginModal({ open, onOpenChange, cats, onDone }) {
  const [catIds, setCatIds] = useState([]);
  const [method, setMethod] = useState("set");
  const [value, setValue] = useState("");
  const [rows, setRows] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);

  const loadLogs = () => dms.bulkEditLog().then(d => setLogs(d.data || [])).catch(() => {});
  useEffect(() => { if (open) { setRows(null); loadLogs(); } }, [open]);

  const toggleCat = (id) => {
    setRows(null);
    setCatIds(catIds.includes(id) ? catIds.filter(x => x !== id) : [...catIds, id]);
  };

  const doPreview = async () => {
    if (catIds.length === 0) { toast.error("Select at least one category"); return; }
    if (value === "" || isNaN(Number(value))) { toast.error("Enter a value"); return; }
    setBusy(true);
    try {
      const res = await dms.bulkMarginPreview({ category_ids: catIds, method, value: Number(value) });
      const rs = res.rows || [];
      setRows(rs);
      const pk = {}; rs.forEach(r => { pk[r.id] = true; }); setPicked(pk);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const doApply = async () => {
    const ids = (rows || []).filter(r => picked[r.id]).map(r => r.id);
    if (ids.length === 0) { toast.error("No products ticked"); return; }
    if (!window.confirm(`Apply margin change to ${ids.length} products and their active price list lines?\n\nYou can undo it from the history below.`)) return;
    setBusy(true);
    try {
      const res = await dms.bulkMarginApply({ product_ids: ids, method, value: Number(value) });
      toast.success(`Margin updated: ${res.products} products, ${res.lines} price list lines`);
      setRows(null); loadLogs(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  };

  const doUndo = async (id) => {
    if (!window.confirm("Undo this bulk change?")) return;
    try {
      const res = await dms.bulkMarginUndo(id);
      toast.success(`Undone. Restored ${res.restored}, skipped ${res.skipped}`);
      loadLogs(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
  };

  const methodLabel = { set: "Set to", add: "Increase by", sub: "Decrease by" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Bulk Edit Margin %</DialogTitle></DialogHeader>

        <div className="text-xs font-semibold text-slate-600 mb-1">1. Choose categories</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {cats.map(c => (
            <label key={c.id} className={`text-xs border rounded px-2 py-1 cursor-pointer ${catIds.includes(c.id) ? "bg-[#faf6e6] border-[#c9a227]" : "border-slate-300"}`}>
              <input type="checkbox" className="mr-1" checked={catIds.includes(c.id)} onChange={() => toggleCat(c.id)} />{c.name}
            </label>
          ))}
        </div>

        <div className="text-xs font-semibold text-slate-600 mb-1">2. Method and value (in percentage points)</div>
        <div className="flex gap-2 items-end mb-3">
          <select value={method} onChange={e => { setMethod(e.target.value); setRows(null); }} className="border border-slate-300 rounded h-9 px-2 text-sm" data-testid="bulk-method">
            <option value="set">Set to</option>
            <option value="add">Increase by</option>
            <option value="sub">Decrease by</option>
          </select>
          <Input type="number" step="0.01" className="w-32" value={value} onChange={e => { setValue(e.target.value); setRows(null); }} placeholder="e.g. 11" data-testid="bulk-value" />
          <Button onClick={doPreview} disabled={busy} variant="outline" className="border-[#c9a227] text-[#8a6600]" data-testid="bulk-preview-btn">Preview</Button>
        </div>

        {rows && (
          <div className="mb-4">
            <div className="text-xs font-semibold text-slate-600 mb-1">3. Preview: nothing is changed until you press Apply ({rows.filter(r => picked[r.id]).length} of {rows.length} ticked)</div>
            <Card className="max-h-[320px] overflow-y-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Pack</TableHead>
                  <TableHead className="text-right">Product margin</TableHead>
                  <TableHead className="text-right">Active price list margin</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.id}>
                      <TableCell><input type="checkbox" checked={!!picked[r.id]} onChange={e => setPicked({ ...picked, [r.id]: e.target.checked })} /></TableCell>
                      <TableCell className="text-xs">{r.category}</TableCell>
                      <TableCell className="text-sm font-medium">{r.name}</TableCell>
                      <TableCell className="text-xs">{r.pack_size || "-"}</TableCell>
                      <TableCell className="text-right text-sm">{r.old_margin}% → <b>{r.new_margin}%</b></TableCell>
                      <TableCell className="text-right text-sm">
                        {r.lines.length === 0 ? "—" : r.lines.map(l => `${l.old}% → ${l.new}%`).join(", ")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No products in the selected categories</div>}
            </Card>
            <div className="flex justify-end mt-3">
              <Button onClick={doApply} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] text-white" data-testid="bulk-apply-btn">Apply to ticked products</Button>
            </div>
          </div>
        )}

        <div className="text-xs font-semibold text-slate-600 mb-1">History (latest 20)</div>
        <Card className="max-h-[200px] overflow-y-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>When</TableHead><TableHead>Change</TableHead><TableHead className="text-right">Products</TableHead><TableHead className="text-right">Lines</TableHead><TableHead className="w-24"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {logs.map(l => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs">{String(l.created_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                  <TableCell className="text-xs">{methodLabel[l.method] || l.method} {l.value}</TableCell>
                  <TableCell className="text-right text-xs">{l.products}</TableCell>
                  <TableCell className="text-right text-xs">{l.lines}</TableCell>
                  <TableCell className="text-right">
                    {l.undone ? <span className="text-[11px] text-slate-400">Undone</span>
                      : <Button size="sm" variant="outline" onClick={() => doUndo(l.id)} data-testid={`bulk-undo-${l.id}`}>Undo</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {logs.length === 0 && <div className="p-4 text-center text-sm text-slate-500">No bulk changes yet</div>}
        </Card>
      </DialogContent>
    </Dialog>
  );
}

// ── Inline: Price history modal (batch list) ──
function PriceHistoryModal({ open, onOpenChange, circulars }) {
  const nav = useNavigate();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Price History — All Batches</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {circulars.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No price batches yet. Click <b>Update Prices</b> to publish the first one.</div>}
          {circulars.map(c => (
            <button key={c.id} onClick={() => { onOpenChange(false); nav(`/dms/owner/price-circulars/${c.id}`); }}
                    className="w-full text-left p-4 rounded-lg border border-[#c9a227]/15 hover:border-[#c9a227] hover:bg-[#faf6e6]/40 transition flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-slate-900">{c.title}</div>
                  {c.is_active && <span className="text-[10px] uppercase font-bold bg-[#c9a227] text-white px-2 py-0.5 rounded">Active</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1">Effective {c.effective_date} · {c.lines_count || 0} products · {c.batch_label}</div>
              </div>
              <ChevronRight size={18} className="text-slate-400" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Owner — Distributors list + detail with KYC + visibility
// ============================================================================
function ValidatedInput({ value, onChange, regex, hint, ...props }) {
  const val = String(value || "").trim();
  const touched = val.length > 0;
  const valid = !touched || !regex || regex.test(val);
  const cls = touched
    ? (valid ? "border-emerald-500 focus-visible:ring-emerald-500" : "border-rose-500 focus-visible:ring-rose-500")
    : "";
  return (
    <div>
      <Input value={value || ""} onChange={onChange} className={cls} {...props} />
      {touched && !valid && hint ? <div className="text-[11px] text-rose-500 mt-1">{hint}</div> : null}
    </div>
  );
}

export function DistributorsPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const nav = useNavigate();

  const load = () => dms.listDistributors().then(d => setList(d.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", email: "", password: "Demo@2026", phone: "", address: "", region: "", gstin: "", pan: "", shop_license: "", bank_name: "", bank_account: "", bank_ifsc: "", credit_limit: 500000 }); setOpen(true); };

  const save = async () => {
    // Full-process onboarding: all details + KYC + at least one document required
    // BEFORE the distributor login is created.
    const rules = {
      name: { label: "Business Name", required: true },
      email: { label: "Login Email", required: true, regex: REGEX.email },
      password: { label: "Login Password", required: true },
      phone: { label: "Phone", required: true, regex: REGEX.phone, hint: "10-digit mobile" },
      gstin: { label: "GSTIN", required: true, regex: REGEX.gstin, hint: "e.g. 22AAAAA0000A1Z5" },
    };
    // Optional details: validate format only if the owner chose to fill them
    const optionalRules = {
      pan: { label: "PAN", required: true, regex: REGEX.pan, hint: "e.g. ABCDE1234F" },
      bank_account: { label: "Bank Account", required: true, regex: REGEX.bankAccount, hint: "9-18 digits" },
      bank_ifsc: { label: "IFSC", required: true, regex: REGEX.ifsc, hint: "e.g. SBIN0001234" },
    };
    Object.keys(optionalRules).forEach(k => { if (String(form[k] || "").trim()) rules[k] = optionalRules[k]; });
    if (String(form.upi_id || "").trim()) rules.upi_id = { label: "UPI ID", required: true, regex: REGEX.upi, hint: "e.g. name@bank" };

    const missing = validateFields(form, rules);
    if (missing.length) {
      toast.error("Missing / invalid: " + missing.join(", "));
      return;
    }
    try {
      await dms.createDistributor(form);
      toast.success("Distributor onboarded");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Distributors"
        subtitle="Onboard distributors and manage KYC + product visibility"
        action={<Button onClick={openNew} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="add-dist-btn"><Plus size={16} className="mr-1" /> New Distributor</Button>}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map(d => (
          <Card key={d.id} className="p-4 hover:shadow-md transition cursor-pointer" onClick={() => nav(`/dms/owner/distributors/${d.id}`)} data-testid={`dist-card-${d.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{d.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{d.email}</div>
                <div className="text-xs text-slate-500">{d.phone}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider bg-[#faf6e6] text-[#a67c00] px-2 py-1 rounded">{d.region || "—"}</span>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">Credit Limit</span>
              <span className="font-semibold text-slate-800">{inr(d.credit_limit)}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-slate-500">GSTIN</span>
              <span className="font-mono text-slate-700">{d.kyc?.gstin || "—"}</span>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-[#a67c00] text-xs font-medium flex items-center">Manage → <ChevronRight size={14} /></div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm(`Delete distributor "${d.name}"? This also removes their login.`)) return;
                  try { await dms.deleteDistributor(d.id); toast.success("Deleted"); load(); }
                  catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
                }}
                className="text-rose-500 hover:text-rose-700 p-1 rounded hover:bg-rose-50"
                title="Delete distributor" data-testid={`del-dist-${d.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          </Card>
        ))}
      </div>
      {list.length === 0 && <EmptyState icon={Handshake} title="No distributors yet" description="Create your first distributor to start receiving orders" />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Distributor + KYC</DialogTitle></DialogHeader>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Only <b>Business Name, Phone, Email, GSTIN and Password</b> are needed to create the login. The distributor can fill the remaining details (address, KYC, bank, documents, location) from <b>My Profile</b> after logging in.
          </div>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Basic Info</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Business Name *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="d-name" /></div>
                <div><Label>Phone *</Label><ValidatedInput value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} regex={REGEX.phone} hint="Enter a valid 10-digit mobile number" /></div>
                <div><Label>Login Email *</Label><ValidatedInput type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} regex={REGEX.email} hint="Enter a valid email address" data-testid="d-email" /></div>
                <div><Label>Login Password *</Label><Input value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
                <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Region</Label><Input value={form.region || ""} onChange={e => setForm({ ...form, region: e.target.value })} /></div>
                <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit || ""} onChange={e => setForm({ ...form, credit_limit: Number(e.target.value) })} /></div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1"><IdCard size={14} /> KYC Details <span className="text-slate-400 normal-case font-normal">(GSTIN required, rest optional)</span></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>GSTIN *</Label><ValidatedInput value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} regex={REGEX.gstin} hint="e.g. 22AAAAA0000A1Z5" /></div>
                <div><Label>PAN</Label><ValidatedInput value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value })} regex={REGEX.pan} hint="e.g. ABCDE1234F" /></div>
                <div><Label>Shop / Trade License</Label><Input value={form.shop_license || ""} onChange={e => setForm({ ...form, shop_license: e.target.value })} /></div>
                <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={e => setForm({ ...form, bank_name: e.target.value })} /></div>
                <div><Label>Bank Account</Label><ValidatedInput value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} regex={REGEX.bankAccount} hint="9-18 digit account number" /></div>
                <div><Label>IFSC</Label><ValidatedInput value={form.bank_ifsc} onChange={e => setForm({ ...form, bank_ifsc: e.target.value })} regex={REGEX.ifsc} hint="e.g. SBIN0001234" /></div>
                <div><Label>Bank Branch</Label><Input value={form.bank_branch || ""} onChange={e => setForm({ ...form, bank_branch: e.target.value })} data-testid="d-branch" /></div>
                <div><Label>UPI ID</Label><ValidatedInput value={form.upi_id} onChange={e => setForm({ ...form, upi_id: e.target.value })} regex={REGEX.upi} hint="e.g. name@bank" placeholder="name@bank" data-testid="d-upi" /></div>
                <div>
                  <Label>Payment QR (image)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {form.qr_url ? <img src={form.qr_url} alt="qr" className="h-10 w-10 object-contain border rounded" /> : null}
                    <input type="file" accept="image/*" data-testid="d-qr" onChange={(e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      const rd = new FileReader(); rd.onload = () => setForm(v => ({ ...v, qr_url: rd.result })); rd.readAsDataURL(f);
                    }} className="text-xs" />
                  </div>
                </div>
              </div>
            </div>
            <LocationDocumentsBlock
              lat={form.gps_lat ?? ""}
              lng={form.gps_lng ?? ""}
              locationLink={form.location_link || ""}
              onLat={(v) => setForm(f => ({ ...f, gps_lat: v === "" ? null : Number(v) }))}
              onLng={(v) => setForm(f => ({ ...f, gps_lng: v === "" ? null : Number(v) }))}
              onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
              documents={form.documents || []}
              onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
              helpText="After the distributor logs in, they can update their exact Latitude / Longitude from their profile if needed."
            />
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-dist-btn">Create Distributor</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function RetailersPage() {
  const [list, setList] = useState([]);
  const [dists, setDists] = useState([]);
  const [sps, setSps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const nav = useNavigate();

  // lite list (no uploaded photos); full details are fetched when Edit is opened
  const load = () => dms.listRetailers({ lite: 1 }).then(d => setList(d.data));
  useEffect(() => { load(); dms.listDistributors().then(d => setDists(d.data)); }, []);
  useEffect(() => {
    if (!form.distributor_id) { setSps([]); return; }
    dms.distSalespersons(form.distributor_id).then(d => setSps(d.data || [])).catch(() => setSps([]));
  }, [form.distributor_id]);

  const [typeFilter, setTypeFilter] = useState("");
  const openNew = () => { setForm({ name: "", phone: "", address: "", region: "", email: "", password: "Demo@2026", gstin: "", shop_license: "", distributor_id: "", salesperson_id: "", credit_limit: 0, retailer_type: "retailer" }); setOpen(true); };

  const openEdit = async (row) => {
    let r = row;
    try {
      r = { ...row, ...(await dms.getRetailer(row.id)) };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load retailer details");
      return;
    }
    setForm({
      id: r.id, name: r.name || "", phone: r.phone || "", address: r.address || "",
      region: r.region || "", email: r.email || "", password: "",
      gstin: r.kyc?.gstin || "", shop_license: r.kyc?.shop_license || "",
      distributor_id: r.distributor_id || "", salesperson_id: r.salesperson_id || "", retailer_type: r.retailer_type || "retailer",
      credit_limit: r.credit_limit || 0, documents: r.documents || [],
      gps_lat: r.gps_lat, gps_lng: r.gps_lng, location_link: r.location_link || "",
      bank_name: r.bank?.bank_name || "", bank_account: r.bank?.bank_account || "",
      bank_ifsc: r.bank?.bank_ifsc || "", upi_id: r.bank?.upi_id || "",
      _hasLogin: !!r.email,
    });
    setOpen(true);
  };

  const save = async () => {
    const rules = {
      name: { label: "Business Name", required: true },
      phone: { label: "Phone", required: true, regex: REGEX.phone, hint: "10-digit mobile" },
      address: { label: "Address", required: true },
      distributor_id: { label: "Distributor", required: true },
      salesperson_id: { label: "Salesperson", required: true },
    };
    if (String(form.email || "").trim() && !form._hasLogin) {
      Object.assign(rules, {
        email: { label: "Login Email", required: true, regex: REGEX.email },
        password: { label: "Login Password", required: true },
        region: { label: "Region", required: true },
        gstin: { label: "GSTIN", required: true, regex: REGEX.gstin, hint: "e.g. 22AAAAA0000A1Z5" },
        shop_license: { label: "Shop License", required: true },
      });
    }
    if (String(form.bank_account || "").trim() || String(form.bank_ifsc || "").trim()) {
      Object.assign(rules, {
        bank_account: { label: "Bank Account", required: true, regex: REGEX.bankAccount, hint: "9-18 digits" },
        bank_ifsc: { label: "IFSC", required: true, regex: REGEX.ifsc, hint: "e.g. SBIN0001234" },
      });
    }
    if (String(form.upi_id || "").trim()) rules.upi_id = { label: "UPI ID", required: true, regex: REGEX.upi, hint: "e.g. name@bank" };

    const missing = validateFields(form, rules);
    if (String(form.email || "").trim() && !form._hasLogin && !(form.documents || []).length) missing.push("at least one Document is required");

    if (missing.length) {
      toast.error(missing.join(", "));
      return;
    }
    try {
      if (form.id) {
        await dms.updateRetailer(form.id, form);
        toast.success("Retailer updated");
      } else {
        await dms.createRetailer(form);
        toast.success("Retailer onboarded");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Retailers"
        subtitle="Onboard retailers and manage KYC + login access"
        action={<Button onClick={openNew} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="add-ret-btn"><Plus size={16} className="mr-1" /> New Retailer</Button>}
      />
      <div className="mb-3 flex items-center gap-2">
        <Label className="text-xs">Retailer Type</Label>
        <select className="border rounded-md h-9 px-2 text-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} data-testid="ret-type-filter">
          <option value="">All types</option>
          <option value="retailer">Retailer</option>
          <option value="wholeseller">Wholeseller</option>
          <option value="workshop">Workshop</option>
          <option value="mechanic">Mechanic</option>
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.filter(r => !typeFilter || (r.retailer_type || "retailer") === typeFilter).map(r => (
          <Card key={r.id} className="p-4 hover:shadow-md transition" data-testid={`ret-card-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.email || "No login"}</div>
                <div className="text-xs text-slate-500">{r.phone}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider bg-[#faf6e6] text-[#a67c00] px-2 py-1 rounded">{r.region || "—"}</span>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">Distributor</span>
              <span className="font-semibold text-slate-800">{dists.find(d => d.id === r.distributor_id)?.name || "—"}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-slate-500">GSTIN</span>
              <span className="font-mono text-slate-700">{r.kyc?.gstin || "—"}</span>
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-slate-500">Type</span>
              <span className="font-semibold text-slate-800">{((r.retailer_type || "retailer").charAt(0).toUpperCase() + (r.retailer_type || "retailer").slice(1))}</span>
            </div>
            <div className="mt-3 flex items-center justify-end gap-1">
              <span className="mr-auto"><ResetLocationButton retailer={r} onDone={load} /></span>
              <button
                onClick={() => openEdit(r)}
                className="text-slate-500 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
                title="Edit retailer" data-testid={`edit-ret-${r.id}`}>
                <Edit size={15} />
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete retailer "${r.name}"? This also removes their login.`)) return;
                  try { await dms.deleteRetailer(r.id); toast.success("Deleted"); load(); }
                  catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
                }}
                className="text-rose-500 hover:text-rose-700 p-1 rounded hover:bg-rose-50"
                title="Delete retailer" data-testid={`del-ret-${r.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          </Card>
        ))}
      </div>
      {list.length === 0 && <EmptyState icon={Handshake} title="No retailers yet" description="Create your first retailer to start secondary orders" />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? `Edit Retailer \u2014 ${form.name}` : "New Retailer + KYC"}</DialogTitle></DialogHeader>
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Name, Phone, Address and Distributor are always required. If you set a Login Email, Region + GSTIN + Shop License + Password + at least one Document also become required.
          </div>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Basic Info</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Business Name *</Label><Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="r-name" /></div>
                <div><Label>Phone *</Label><ValidatedInput value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} regex={REGEX.phone} hint="Enter a valid 10-digit mobile number" /></div>
                <div className="col-span-2">
                  <Label>Distributor *</Label>
                  <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.distributor_id || ""} onChange={e => setForm({ ...form, distributor_id: e.target.value, salesperson_id: "" })} data-testid="r-dist">
                    <option value="">Select distributor</option>
                    {dists.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label>Salesperson *</Label>
                  <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.salesperson_id || ""} onChange={e => setForm({ ...form, salesperson_id: e.target.value })} data-testid="r-sp" disabled={!form.distributor_id}>
                    <option value="">{form.distributor_id ? (sps.length ? "Select salesperson" : "No salespersons assigned to this distributor") : "Select a distributor first"}</option>
                    {sps.map(s => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
                  </select>
                </div>
                <div className="col-span-2"><Label>Address *</Label><Textarea rows={2} value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
                <div><Label>Region</Label><Input value={form.region || ""} onChange={e => setForm({ ...form, region: e.target.value })} /></div>
                <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit || ""} onChange={e => setForm({ ...form, credit_limit: Number(e.target.value) })} /></div>
                <div className="col-span-2"><Label>Retailer Type *</Label>
                  <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.retailer_type || "retailer"} onChange={e => setForm({ ...form, retailer_type: e.target.value })} data-testid="r-type">
                    <option value="retailer">Retailer</option>
                    <option value="wholeseller">Wholeseller</option>
                    <option value="workshop">Workshop</option>
                    <option value="mechanic">Mechanic</option>
                  </select></div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Login {form._hasLogin ? "(active)" : "(optional)"}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Login Email</Label><ValidatedInput type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} regex={REGEX.email} hint="Enter a valid email address" disabled={form._hasLogin} data-testid="r-email" /></div>
                <div><Label>Login Password {form._hasLogin ? "" : ""}</Label><Input value={form.password || ""} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={form._hasLogin ? "Use Reset Password in User Management" : ""} disabled={form._hasLogin} /></div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1"><IdCard size={14} /> KYC Details</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>GSTIN</Label><ValidatedInput value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} regex={REGEX.gstin} hint="e.g. 22AAAAA0000A1Z5" /></div>
                <div><Label>Shop / Trade License</Label><Input value={form.shop_license || ""} onChange={e => setForm({ ...form, shop_license: e.target.value })} /></div>
                <div><Label>Bank Name</Label><Input value={form.bank_name || ""} onChange={e => setForm({ ...form, bank_name: e.target.value })} /></div>
                <div><Label>Bank Account</Label><ValidatedInput value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} regex={REGEX.bankAccount} hint="9-18 digit account number" /></div>
                <div><Label>IFSC</Label><ValidatedInput value={form.bank_ifsc} onChange={e => setForm({ ...form, bank_ifsc: e.target.value })} regex={REGEX.ifsc} hint="e.g. SBIN0001234" /></div>
                <div><Label>UPI ID</Label><ValidatedInput value={form.upi_id} onChange={e => setForm({ ...form, upi_id: e.target.value })} regex={REGEX.upi} hint="e.g. name@bank" placeholder="name@bank" /></div>
              </div>
            </div>
            <LocationDocumentsBlock
              lat={form.gps_lat ?? ""}
              lng={form.gps_lng ?? ""}
              locationLink={form.location_link || ""}
              onLat={(v) => setForm(f => ({ ...f, gps_lat: v === "" ? null : Number(v) }))}
              onLng={(v) => setForm(f => ({ ...f, gps_lng: v === "" ? null : Number(v) }))}
              onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
              documents={form.documents || []}
              onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
              helpText="Required only if you're setting up a login for this retailer."
            />
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-ret-btn">{form.id ? "Save Changes" : "Create Retailer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DistributorDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [vis, setVis] = useState([]);
  const [tab, setTab] = useState("kyc");
  const [editKyc, setEditKyc] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    const [dd, vv] = await Promise.all([dms.getDistributor(id), dms.getDistVisibility(id)]);
    setD(dd); setVis(vv.data);
    setForm({ ...dd, ...(dd.kyc || {}), ...(dd.bank || {}) });
  };
  useEffect(() => { load(); }, [id]);

  const del = async () => {
    if (!window.confirm(`Delete distributor "${d.name}"? This also removes their login. This cannot be undone.`)) return;
    try {
      await dms.deleteDistributor(id);
      toast.success("Distributor deleted");
      nav("/dms/owner/distributors");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const saveKyc = async () => {
    try {
      const kyc = ["gstin", "pan", "aadhaar", "shop_license", "bank_name", "bank_account", "bank_ifsc", "notes"].reduce((a, k) => (a[k] = form[k] || "", a), {});
      const bank = {
        gstin: form.gstin || "", bank_name: form.bank_name || "", bank_account: form.bank_account || "",
        bank_ifsc: form.bank_ifsc || "", bank_branch: form.bank_branch || "",
        upi_id: form.upi_id || "", upi_name: form.upi_name || "", qr_url: form.qr_url || "",
      };
      await dms.updateDistributor(id, {
        phone: form.phone, address: form.address, region: form.region, credit_limit: Number(form.credit_limit || 0), kyc, bank,
        documents: form.documents || [], gps_lat: form.gps_lat, gps_lng: form.gps_lng, location_link: form.location_link,
      });
      toast.success("Updated"); setEditKyc(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleVisibility = async (pid, visible) => {
    try { await dms.setDistVisibility(id, { product_id: pid, visible }); setVis(vis.map(v => v.product_id === pid ? { ...v, visible } : v)); }
    catch (e) { toast.error("Failed"); }
  };

  if (!d) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  return (
    <div>
      <PageHeader
        title={d.name}
        subtitle={`${d.email} • ${d.region || "—"} • Credit ${inr(d.credit_limit)}`}
        back="/dms/owner/distributors"
        action={<Button variant="outline" size="sm" onClick={del} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="delete-distributor-btn"><Trash2 size={14} className="mr-1" /> Delete</Button>}
      />
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        {[
          { k: "kyc", label: "KYC & Profile" },
          { k: "visibility", label: "Product Visibility" },
          { k: "pricelists", label: "Price Lists" },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? "border-[#a67c00] text-[#a67c00]" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`tab-${t.k}`}>{t.label}</button>
        ))}
      </div>

      {tab === "kyc" && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">KYC Documents & Profile</h3>
            {!editKyc && <Button variant="outline" size="sm" onClick={() => setEditKyc(true)} data-testid="edit-kyc-btn"><Edit size={14} className="mr-1" /> Edit</Button>}
          </div>
          {!editKyc ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {[
                ["Business Name", d.name], ["Phone", d.phone], ["Email", d.email],
                ["Address", d.address], ["Region", d.region], ["Credit Limit", inr(d.credit_limit)],
                ["GSTIN", d.kyc?.gstin], ["PAN", d.kyc?.pan], ["Shop License", d.kyc?.shop_license],
                ["Bank Name", d.bank?.bank_name || d.kyc?.bank_name], ["Account No.", d.bank?.bank_account || d.kyc?.bank_account], ["IFSC", d.bank?.bank_ifsc || d.kyc?.bank_ifsc],
                ["Bank Branch", d.bank?.bank_branch], ["UPI ID", d.bank?.upi_id],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 py-1.5">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-900 font-medium">{v || "—"}</span>
                </div>
              ))}
              {d.bank?.qr_url && (
                <div className="md:col-span-2 mt-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-semibold">Payment QR</div>
                  <img src={d.bank.qr_url} alt="Payment QR" className="h-32 w-32 object-contain border rounded" data-testid="dist-qr-view" />
                </div>
              )}
              <div className="md:col-span-2 mt-2">
                <DocumentsGallery documents={d.documents || []} locationLink={d.location_link} lat={d.gps_lat} lng={d.gps_lng} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {["name", "phone", "address", "region", "credit_limit", "gstin", "pan", "shop_license", "bank_name", "bank_account", "bank_ifsc", "bank_branch", "upi_id"].map(k => (
                <div key={k} className={k === "address" ? "col-span-2" : ""}><Label className="capitalize">{k.replace(/_/g, " ")}</Label><Input value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} disabled={k === "name"} className={k === "name" ? "bg-slate-100 cursor-not-allowed" : ""} data-testid={k === "name" ? "dist-name-locked" : undefined} />{k === "name" && <div className="text-[11px] text-slate-500 mt-1">Name cannot be changed</div>}</div>
              ))}
              <div className="col-span-2">
                <Label>Payment QR (image)</Label>
                <div className="flex items-center gap-2 mt-1">
                  {form.qr_url ? <img src={form.qr_url} alt="qr" className="h-12 w-12 object-contain border rounded" /> : null}
                  <input type="file" accept="image/*" onChange={(e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const rd = new FileReader(); rd.onload = () => setForm(v => ({ ...v, qr_url: rd.result })); rd.readAsDataURL(f);
                  }} className="text-xs" />
                  {form.qr_url && <Button variant="outline" size="sm" onClick={() => setForm(v => ({ ...v, qr_url: "" }))}>Remove</Button>}
                </div>
              </div>
              <div className="col-span-2">
                <LocationDocumentsBlock
                  lat={form.gps_lat} lng={form.gps_lng} locationLink={form.location_link}
                  onLat={(v) => setForm(f => ({ ...f, gps_lat: v }))}
                  onLng={(v) => setForm(f => ({ ...f, gps_lng: v }))}
                  onLocationLink={(v) => setForm(f => ({ ...f, location_link: v }))}
                  documents={form.documents || []}
                  onDocuments={(docs) => setForm(f => ({ ...f, documents: docs }))}
                />
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setEditKyc(false)}>Cancel</Button>
                <Button onClick={saveKyc} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-kyc-btn">Save</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {tab === "pricelists" && <DistPriceListsTab id={id} />}
      {tab === "visibility" && (
        <Card>
          <div className="p-4 border-b border-slate-100">
            <div className="text-sm text-slate-600">Toggle products this distributor is allowed to see. Turning OFF a product hides it from their catalog immediately.</div>
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Visible</TableHead></TableRow></TableHeader>
            <TableBody>
              {vis.map(v => (
                <TableRow key={v.product_id}>
                  <TableCell className="font-mono text-xs">{v.sku_code}</TableCell>
                  <TableCell>{v.product_name}</TableCell>
                  <TableCell className="text-right"><Switch checked={v.visible} onCheckedChange={c => toggleVisibility(v.product_id, c)} data-testid={`vis-${v.product_id}`} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Owner — Primary Orders list + detail (fulfillment)
// ============================================================================
export function OwnerPrimaryOrdersPage() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();
  const load = () => dms.listOrders(filter || undefined).then(d => setList(d.data));
  useEffect(() => { load(); }, [filter]);

  const doDelete = async (e, o) => {
    e.stopPropagation();
    const warn = o.status === "ready_to_go"
      ? " This order was already marked Ready to Go — its stock deduction and e-Bill will be reversed."
      : "";
    if (!window.confirm(`Delete order ${o.order_no}?${warn}\n\nThis cannot be undone.`)) return;
    try {
      await dms.deleteOrder(o.id);
      toast.success(`${o.order_no} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete order");
    }
  };

  return (
    <div>
      <PageHeader title="Primary Orders" subtitle="Orders from distributors — fulfill and dispatch"
        action={<Button onClick={() => nav("/dms/owner/primary-orders/create")} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="owner-create-order-cta"><ShoppingCart size={16} className="mr-1" /> Create Order</Button>}
      />
      <div className="flex gap-2 mb-4 flex-wrap">
        {[{ k: "", label: "All" }, { k: "pending", label: "Pending" }, { k: "partially_fulfilled", label: "Partial" }, { k: "fulfilled", label: "Fulfilled" }, { k: "ready_to_go", label: "Ready" }, { k: "received", label: "Received" }].map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 py-1.5 text-xs rounded-full border ${filter === f.k ? "bg-[#c9a227] text-white border-[#a67c00]" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`} data-testid={`filter-${f.k || "all"}`}>{f.label}</button>
        ))}
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Order #</TableHead><TableHead>Distributor</TableHead><TableHead>Items</TableHead>
            <TableHead>Total</TableHead><TableHead>Fulfillment</TableHead><TableHead>Status</TableHead><TableHead>Placed</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {list.map(o => (
              <TableRow key={o.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/dms/owner/primary-orders/${o.id}`)} data-testid={`ord-row-${o.id}`}>
                <TableCell className="font-mono text-sm">{o.order_no}</TableCell>
                <TableCell className="font-medium">{o.distributor_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{o.items.length}</TableCell>
                <TableCell className="font-semibold">{inr(o.total)}</TableCell>
                <TableCell><div className="flex items-center gap-2 min-w-[100px]"><div className="w-16 h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0" style={{ width: `${o.fulfillment_pct}%` }} /></div><span className="text-xs text-slate-600">{o.fulfillment_pct}%</span></div></TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPill(o.status)}`}>{o.status.replace(/_/g, " ")}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{niceDate(o.created_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <button onClick={(e) => { e.stopPropagation(); nav(`/dms/owner/primary-orders/${o.id}`); }} className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center" data-testid={`edit-order-${o.id}`} title="Edit order">
                      <Edit size={14} />
                    </button>
                    {o.status !== "received" && (
                      <button onClick={(e) => doDelete(e, o)} className="h-8 w-8 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center" data-testid={`delete-order-${o.id}`} title="Delete order">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {list.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No orders</div>}
      </Card>
    </div>
  );
}

// ============================================================================
// Owner — Create Order for a Distributor (browse + place, mirrors Distributor's own flow)
// ============================================================================
export function OwnerCreateOrderPage() {
  const nav = useNavigate();
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [openCategory, setOpenCategory] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { dms.listDistributors().then(d => setDistributors(d.data || d || [])).catch(() => {}); }, []);

  useEffect(() => {
    setProducts([]); setCart({}); setOpenCategory(null);
    if (!distributorId) return;
    dms.browseProducts(distributorId).then(d => setProducts(d.data || [])).catch(() => toast.error("Failed to load products"));
  }, [distributorId]);

  const setQty = (pid, delta) => setCart(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) + delta) }));
  const setQtyDirect = (pid, val) => setCart(prev => ({ ...prev, [pid]: Math.max(0, Number(val) || 0) }));

  const subtotal = products.reduce((s, p) => s + (cart[p.id] || 0) * p.unit_price, 0);
  const gstTotal = products.reduce((s, p) => s + (cart[p.id] || 0) * p.unit_price * (p.gst_pct / 100), 0);
  const total = subtotal + gstTotal;
  const itemsCount = Object.values(cart).filter(v => v > 0).length;
  const cartItems = Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, qty_pcs]) => ({ product_id, qty_pcs }));

  const openConfirm = () => {
    if (cartItems.length === 0) return toast.error("Add at least one product");
    setConfirmOpen(true);
  };

  const doPlace = async () => {
    setBusy(true);
    try {
      const payloadItems = cartItems.map(ci => {
        const p = products.find(x => x.id === ci.product_id);
        const boxQty = p?.box_qty || 1;
        return { product_id: ci.product_id, qty_boxes: Math.round((ci.qty_pcs / boxQty) * 100) / 100 };
      });
      const o = await dms.placeOrder({ distributor_id: distributorId, items: payloadItems, notes });
      toast.success(`Order ${o.order_no} created!`);
      setConfirmOpen(false);
      nav(`/dms/owner/primary-orders/${o.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const searched = search.trim()
    ? products.filter(p => p.name.toLowerCase().includes(search.trim().toLowerCase()) || (p.sku_code || "").toLowerCase().includes(search.trim().toLowerCase()))
    : products;
  const grouped = {};
  searched.forEach(p => { (grouped[p.category_name || "Uncategorised"] ||= []).push(p); });
  const categoryList = Object.entries(grouped).map(([name, prods]) => {
    const inCart = prods.reduce((n, p) => n + (cart[p.id] > 0 ? 1 : 0), 0);
    return { name, count: prods.length, inCart };
  });

  return (
    <div className="pb-32">
      <PageHeader
        title="Create Order for Distributor"
        subtitle="Pick a distributor, then browse or search products to order on their behalf"
        back="/dms/owner/primary-orders"
      />

      <Card className="p-4 mb-4">
        <Label>Distributor*</Label>
        <Select value={distributorId} onValueChange={v => setDistributorId(v)}>
          <SelectTrigger><SelectValue placeholder="Choose distributor" /></SelectTrigger>
          <SelectContent>{distributors.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
        </Select>
      </Card>

      {!distributorId && (
        <div className="p-8 text-center text-sm text-slate-500">Select a distributor to start browsing products.</div>
      )}

      {distributorId && (
        <>
          <div className="mb-4">
            <Input placeholder="Search products by name or SKU…" value={search} onChange={e => { setSearch(e.target.value); setOpenCategory(null); }} data-testid="owner-order-search" />
          </div>

          {/* When searching, show a flat matching list regardless of category */}
          {search.trim() ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {searched.length === 0 && <div className="col-span-full p-8 text-center text-sm text-slate-500">No products match "{search}"</div>}
              {searched.map(p => {
                const q = cart[p.id] || 0;
                return (
                  <Card key={p.id} className="p-4 flex flex-col" data-testid={`product-card-${p.id}`}>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 leading-tight">{p.name}</div>
                      <div className="text-xs font-mono text-slate-500 mb-2">{p.sku_code} • {p.category_name}</div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-lg font-bold text-[#a67c00]">{inr(Math.round(p.unit_price))}</span>
                        <span className="text-xs text-slate-500">/ nos (excl. GST)</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">+{p.gst_pct}% GST • Owner stock: {p.owner_stock_boxes} boxes</div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setQty(p.id, -1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Minus size={16} /></button>
                        <input type="number" min={0} value={q} onChange={e => setQtyDirect(p.id, e.target.value)} className="w-14 h-9 text-center border border-slate-200 rounded-lg text-sm font-semibold" />
                        <button onClick={() => setQty(p.id, 1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center bg-[#faf6e6] text-[#a67c00]"><Plus size={16} /></button>
                      </div>
                      <div className="text-right ml-auto">
                        {q > 0 && <div className="text-sm font-semibold text-slate-900">{inr(Math.round(q * p.unit_price))}</div>}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          ) : (
            <>
              {!openCategory && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {categoryList.length === 0 && <div className="col-span-full p-8 text-center text-sm text-slate-500">No products available for this distributor.</div>}
                  {categoryList.map(c => (
                    <button key={c.name} onClick={() => setOpenCategory(c.name)}
                      className="text-left bg-white border border-slate-200 hover:border-[#c9a227] hover:shadow-md transition rounded-2xl p-5 relative">
                      <div className="h-10 w-10 rounded-xl bg-[#faf6e6] text-[#a67c00] flex items-center justify-center mb-3"><Package size={20} /></div>
                      <div className="font-semibold text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.count} product{c.count !== 1 ? "s" : ""}</div>
                      {c.inCart > 0 && <span className="absolute top-3 right-3 bg-[#c9a227] text-white text-[10px] font-bold rounded-full px-2 py-0.5">{c.inCart} in cart</span>}
                      <div className="mt-4 text-[#a67c00] text-xs font-medium flex items-center">Open <ChevronRight size={12} className="ml-0.5" /></div>
                    </button>
                  ))}
                </div>
              )}
              {openCategory && (
                <>
                  <Button variant="outline" onClick={() => setOpenCategory(null)} className="mb-3"><ChevronRight className="rotate-180 mr-1" size={16} /> All Categories</Button>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(grouped[openCategory] || []).map(p => {
                      const q = cart[p.id] || 0;
                      return (
                        <Card key={p.id} className="p-4 flex flex-col">
                          <div className="flex-1">
                            <div className="font-semibold text-slate-900 leading-tight">{p.name}</div>
                            <div className="text-xs font-mono text-slate-500 mb-2">{p.sku_code} • {p.box_qty} bottles/box</div>
                            <div className="flex items-baseline gap-2 mt-2">
                              <span className="text-lg font-bold text-[#a67c00]">{inr(Math.round(p.unit_price))}</span>
                              <span className="text-xs text-slate-500">/ nos (excl. GST)</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">+{p.gst_pct}% GST • Owner stock: {p.owner_stock_boxes} boxes</div>
                          </div>
                          <div className="mt-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1">
                              <button onClick={() => setQty(p.id, -1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><Minus size={16} /></button>
                              <input type="number" min={0} value={q} onChange={e => setQtyDirect(p.id, e.target.value)} className="w-14 h-9 text-center border border-slate-200 rounded-lg text-sm font-semibold" />
                              <button onClick={() => setQty(p.id, 1)} className="h-9 w-9 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center bg-[#faf6e6] text-[#a67c00]"><Plus size={16} /></button>
                            </div>
                            <div className="text-right ml-auto">
                              {q > 0 && <div className="text-sm font-semibold text-slate-900">{inr(Math.round(q * p.unit_price))}</div>}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          <div className="fixed bottom-0 left-0 lg:left-60 right-0 bg-white border-t border-slate-200 shadow-lg z-40">
            <div className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="text-xs text-slate-500">{itemsCount} items • Subtotal {inr(subtotal)} + GST {inr(gstTotal)}</div>
                <div className="text-xl font-bold text-slate-900">{inr(total)}</div>
              </div>
              <div className="hidden md:block flex-1 max-w-sm"><Input placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} /></div>
              <Button disabled={itemsCount === 0 || busy} onClick={openConfirm} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white h-11 px-6">
                <ShoppingCart size={16} className="mr-2" /> Create Order
              </Button>
            </div>
          </div>

          <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Confirm Order</DialogTitle></DialogHeader>
              <div className="max-h-64 overflow-y-auto space-y-2 my-2">
                {cartItems.map(ci => {
                  const p = products.find(x => x.id === ci.product_id);
                  if (!p) return null;
                  return (
                    <div key={ci.product_id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                      <div>
                        <div className="font-medium text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500">{ci.qty_pcs} pc{ci.qty_pcs !== 1 ? "s" : ""} × {inr(p.unit_price)}</div>
                      </div>
                      <div className="font-semibold text-slate-900">{inr(ci.qty_pcs * p.unit_price)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between text-base font-bold border-t border-slate-200 pt-3">
                <span>Total</span><span>{inr(total)}</span>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
                <Button variant="outline" onClick={() => setConfirmOpen(false)} className="w-full sm:w-auto">Edit</Button>
                <Button onClick={doPlace} disabled={busy} className="w-full sm:w-auto bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white">
                  <ShoppingCart size={16} className="mr-2" /> Confirm & Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

export function OwnerOrderDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attachUrl, setAttachUrl] = useState("");
  const [attachName, setAttachName] = useState("");
  const [ewayInput, setEwayInput] = useState("");
  const [savingEway, setSavingEway] = useState(false);
  const [editNoOpen, setEditNoOpen] = useState(false);
  const [noPrefix, setNoPrefix] = useState("");
  const [noNumber, setNoNumber] = useState("");
  const [noKnownPrefixes, setNoKnownPrefixes] = useState([]);
  const [noAddingPrefix, setNoAddingPrefix] = useState(false);
  const [noNewPrefix, setNoNewPrefix] = useState("");
  const [savingNo, setSavingNo] = useState(false);
  const [customEbillNo, setCustomEbillNo] = useState("");
  const [customInvoiceDate, setCustomInvoiceDate] = useState("");
  const [ebPrefix, setEbPrefix] = useState("");
  const [ebNumber, setEbNumber] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addProds, setAddProds] = useState([]);
  const [addPid, setAddPid] = useState("");
  const [addSearch, setAddSearch] = useState("");
  const [addBoxes, setAddBoxes] = useState("1");
  const [genEway, setGenEway] = useState("");
  const [genTMode, setGenTMode] = useState("");
  const [genTVehicle, setGenTVehicle] = useState("");
  const [genTTransporter, setGenTTransporter] = useState("");
  const [genTLr, setGenTLr] = useState("");
  const [genNotes, setGenNotes] = useState("");
  const [genDesc, setGenDesc] = useState("");
  const [poMetaOpen, setPoMetaOpen] = useState(false);
  const [poNo, setPoNo] = useState("");
  const [poDate, setPoDate] = useState("");
  const [savingPoMeta, setSavingPoMeta] = useState(false);

  const load = () => dms.getOrder(id).then(o => { setOrder(o); setEwayInput(o.ebill?.eway_bill_no || ""); });
  useEffect(() => { load(); }, [id]);

  const openEditPoMeta = () => {
    setPoNo(order.order_no || "");
    setPoDate((order.created_at || "").slice(0, 10));
    setPoMetaOpen(true);
  };

  const savePoMeta = async () => {
    setSavingPoMeta(true);
    try {
      await dms.updatePrimaryOrderMeta(order.id, { order_no: poNo.trim(), date: poDate });
      toast.success("PO number/date updated");
      setPoMetaOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setSavingPoMeta(false);
  };

  const saveEwayBill = async () => {
    setSavingEway(true);
    try { await dms.updateEwayBill(order.ebill.id, { eway_bill_no: ewayInput.trim() }); toast.success("e-Way Bill number saved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    setSavingEway(false);
  };

  const openEditEbillNo = async () => {
    const no = order.ebill.ebill_no || "";
    const idx = no.lastIndexOf("-");
    setNoPrefix(idx === -1 ? "" : no.slice(0, idx));
    setNoNumber(idx === -1 ? no : no.slice(idx + 1));
    setNoAddingPrefix(false); setNoNewPrefix("");
    try { const s = await dms.getNumberSettings(); setNoKnownPrefixes(s.ebill?.known_prefixes || []); } catch (e) { /* ignore */ }
    setEditNoOpen(true);
  };

  const saveEbillNo = async () => {
    const num = noNumber.trim();
    if (!num) { toast.error("Number is required"); return; }
    const newNo = noPrefix ? `${noPrefix}-${num}` : num;
    setSavingNo(true);
    try {
      await dms.updateEbillNumber(order.ebill.id, newNo);
      try { await dms.updateNumberSettings("ebill", { prefix: noPrefix }); } catch (e) { /* non-fatal */ }
      toast.success("e-Bill number updated");
      setEditNoOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setSavingNo(false);
  };

  const setLine = async (pid, qty, allowOversell = true, allocations = null) => {
    setBusy(true);
    try {
      await dms.fulfillLine(id, { product_id: pid, qty_boxes_fulfilled: Number(qty), allow_oversell: allowOversell, ...(allocations ? { allocations } : {}) });
      await load();
    }
    catch (e) {
      if (e.response?.status === 409) {
        setBusy(false);
        const msg = (e.response?.data?.detail || "Insufficient owner stock.") + "\n\nDo you want to fulfill this quantity anyway?";
        if (window.confirm(msg)) { return setLine(pid, qty, true, allocations); }
        await load(); // revert the input back to the saved value
        return;
      }
      toast.error(e.response?.data?.detail || "Failed");
    }
    setBusy(false);
  };

  useEffect(() => {
    dms.getNumberSettings().then(s => setEbPrefix(s.ebill?.prefix || "EB")).catch(() => setEbPrefix("EB"));
  }, []);

  // Save every changed line in one go (per-row Save buttons stay as they are)
  const saveAll = async () => {
    const rows = [];
    for (const it of order.items) {
      const bq = it.box_qty || 1;
      const savedPcs = Math.round(it.qty_boxes_fulfilled * bq);
      const bxEl = document.querySelector(`[data-testid="fill-bx-${it.product_id}"]`);
      const pcEl = document.querySelector(`[data-testid="fill-pcs-${it.product_id}"]`);
      if (!bxEl || !pcEl) continue;
      let total = Number(bxEl.value || 0) * bq + Number(pcEl.value || 0);
      const lots = it.lots || [];
      let allocs = null;
      if (lots.length > 1) {
        const picked = lots
          .map(l => ({ batch_no: l.batch_no, pcs: Number(document.querySelector(`[data-testid="batch-${it.product_id}-${l.batch_no}"]`)?.value || 0) }))
          .filter(x => x.pcs > 0);
        if (picked.length) {
          const sum = picked.reduce((a, x) => a + x.pcs, 0);
          total = sum;
          const q = Math.round((total / bq) * 10000) / 10000;
          let used = 0;
          allocs = picked.map((x, i) => {
            if (i === picked.length - 1) return { batch_no: x.batch_no, qty_boxes: Math.round((q - used) * 10000) / 10000 };
            const v = Math.round((x.pcs / bq) * 10000) / 10000;
            used += v;
            return { batch_no: x.batch_no, qty_boxes: v };
          });
        }
      }
      if (total !== savedPcs || allocs) rows.push({ it, qty: total / bq, allocs });
    }
    if (!rows.length) { toast.info("No changes to save"); return; }

    setBusy(true);
    const okList = [];
    const failed = [];
    for (const r of rows) {
      const name = r.it.product_name;
      const payload = (over) => ({ product_id: r.it.product_id, qty_boxes_fulfilled: Number(r.qty), allow_oversell: over, ...(r.allocs ? { allocations: r.allocs } : {}) });
      try {
        await dms.fulfillLine(id, payload(true));
        okList.push(name);
      } catch (e) {
        if (e.response?.status === 409) {
          const msg = `${name}\n` + (e.response?.data?.detail || "Insufficient owner stock.") + "\n\nDo you want to fulfill this quantity anyway?";
          if (window.confirm(msg)) {
            try { await dms.fulfillLine(id, payload(true)); okList.push(name); }
            catch (e2) { failed.push(`${name}: ${e2.response?.data?.detail || "Failed"}`); }
          } else {
            failed.push(`${name}: skipped (low stock)`);
          }
        } else {
          failed.push(`${name}: ${e.response?.data?.detail || "Failed"}`);
        }
      }
    }
    await load();
    setBusy(false);
    if (okList.length) toast.success(`${okList.length} line(s) saved`);
    if (failed.length) toast.error(`${failed.length} not saved: ${failed.join(" | ")}`);
  };

  const openAddPoItem = async () => {
    setAddPid(""); setAddSearch(""); setAddBoxes("1");
    try {
      const r = await dms.listProducts();
      const have = new Set(order.items.map(x => x.product_id));
      setAddProds((r.data || r || []).filter(pr => !have.has(pr.id)));
    } catch (e) { toast.error("Failed to load products"); setAddProds([]); }
    setAddOpen(true);
  };

  const confirmAddPoItem = async () => {
    const qty = Number(addBoxes || 0);
    if (!addPid) { toast.error("Select a product"); return; }
    if (qty <= 0) { toast.error("Enter boxes"); return; }
    setBusy(true);
    try {
      await dms.addPrimaryItem(id, { product_id: addPid, qty_boxes: qty });
      toast.success("Item added");
      setAddOpen(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add item"); }
    setBusy(false);
  };

  const removePoItem = async (it) => {
    if (!window.confirm(`Remove ${it.product_name} from this order?\n\nThis line will not create a backorder.`)) return;
    setBusy(true);
    try {
      await dms.removePrimaryItem(id, { product_id: it.product_id });
      toast.success("Item removed");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
    setBusy(false);
  };

  const ready = async () => {
    if (!window.confirm("Mark this order Ready to Go? This will generate the e-Bill and reduce your inventory.")) return;
    setBusy(true);
    try {
      await dms.markReady(id, {
        ebill_no: ebNumber.trim() ? (ebPrefix.trim() ? `${ebPrefix.trim()}-${ebNumber.trim()}` : ebNumber.trim()) : "", invoice_date: customInvoiceDate,
        eway_bill_no: genEway.trim(),
        transport: { mode: genTMode, vehicle_no: genTVehicle, transporter: genTTransporter, lr_no: genTLr },
        notes: genNotes, description: genDesc,
      });
      toast.success("Order marked Ready to Go — e-Bill generated");
      setCustomEbillNo("");
      await load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const unready = async () => {
    if (!window.confirm("Unlock this order for editing? This will reverse the stock deduction and delete the e-Bill — you can then correct fulfilled quantities and mark it Ready to Go again.")) return;
    setBusy(true);
    try { await dms.unreadyOrder(id); toast.success("Order unlocked — you can edit fulfilled quantities now"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const doDelete = async () => {
    const warn = order.status === "ready_to_go"
      ? "This order was already marked Ready to Go — its stock deduction and e-Bill will be reversed, then the order deleted."
      : "This cannot be undone.";
    if (!window.confirm(`Delete order ${order.order_no}?\n\n${warn}`)) return;
    setBusy(true);
    try { await dms.deleteOrder(id); toast.success(`${order.order_no} deleted`); nav("/dms/owner/primary-orders"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete order"); setBusy(false); }
  };

  const uploadAttachment = async () => {
    if (!attachUrl) return;
    try { await dms.addAttachment({ reference_id: id, kind: "invoice", name: attachName || "Invoice", url: attachUrl }); setAttachUrl(""); setAttachName(""); load(); toast.success("Attached"); }
    catch { toast.error("Failed"); }
  };

  if (!order) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const canFulfill = order.status !== "received" && order.status !== "ready_to_go";
  const canReady = ["pending", "partially_fulfilled", "fulfilled"].includes(order.status) && order.items.some(it => it.qty_boxes_fulfilled > 0);

  return (
    <div>
      {canReady && (
        <Card className="p-4 mb-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <div className="flex gap-2">
                <div>
                  <Label>Invoice Prefix</Label>
                  <Input value={ebPrefix} onChange={e => setEbPrefix(e.target.value)} className="w-28 mt-1" data-testid="ebill-prefix-input" />
                </div>
                <div>
                  <Label>Invoice No (blank = auto)</Label>
                  <Input value={ebNumber} onChange={e => setEbNumber(e.target.value)} placeholder="Auto-generate" className="w-40 mt-1" data-testid="ebill-number-input" />
                </div>
              </div>
            </div>
            <div>
              <Label>Invoice Date (optional — leave blank for today)</Label>
              <Input
                type="date"
                value={customInvoiceDate}
                onChange={e => setCustomInvoiceDate(e.target.value)}
                className="max-w-xs mt-1"
                data-testid="custom-invoice-date-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div className="md:col-span-4"><Label>E-Way Bill No.</Label><Input value={genEway} onChange={e => setGenEway(e.target.value)} className="mt-1 max-w-md" data-testid="gen-eway-input" /></div>
            <div><Label>Transport Mode (Road / Rail)</Label><Input value={genTMode} onChange={e => setGenTMode(e.target.value)} className="mt-1" /></div>
            <div><Label>Vehicle No.</Label><Input value={genTVehicle} onChange={e => setGenTVehicle(e.target.value)} className="mt-1" /></div>
            <div><Label>Transporter</Label><Input value={genTTransporter} onChange={e => setGenTTransporter(e.target.value)} className="mt-1" /></div>
            <div><Label>LR / Docket No.</Label><Input value={genTLr} onChange={e => setGenTLr(e.target.value)} className="mt-1" /></div>
            <div className="md:col-span-2"><Label>Notes</Label><Input value={genNotes} onChange={e => setGenNotes(e.target.value)} className="mt-1" /></div>
            <div className="md:col-span-2"><Label>Description</Label><Input value={genDesc} onChange={e => setGenDesc(e.target.value)} className="mt-1" /></div>
          </div>
        </Card>
      )}
      <PageHeader
        title={order.order_no}
        subtitle={
          order.is_backorder
            ? `${order.distributor_name} • Backorder from ${order.backorder_of_no || "previous order"} • Placed ${niceDate(order.created_at)}`
            : `${order.distributor_name} • Placed ${niceDate(order.created_at)}`
        }
        back="/dms/owner/primary-orders"
        action={<div className="flex gap-2">
          {order.is_backorder && <span className="self-center text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200">Backorder</span>}
          {order.ebill_id && <Button variant="outline" onClick={() => window.open(`/dms/print/purchase-order/${order.id}`, "_blank")} data-testid="print-po-btn"><span className="mr-1">🖨</span> Print PO</Button>}
          <Button variant="outline" onClick={openEditPoMeta} data-testid="edit-po-meta-btn"><span className="mr-1">✏️</span> Edit PO No/Date</Button>
          {order.ebill_id && <Button variant="outline" onClick={() => window.open(`/dms/print/ebill/${order.ebill_id}`, "_blank")} data-testid="print-ebill-btn"><span className="mr-1">🖨</span> Print e-Bill</Button>}
          {canReady && <Button onClick={ready} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="mark-ready-btn"><Truck size={16} className="mr-1" /> Generate Invoice</Button>}
          {order.status === "ready_to_go" && <Button variant="outline" onClick={unready} disabled={busy} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="unready-btn">Undo — Unlock to Edit</Button>}
          {order.status !== "received" && <Button variant="outline" onClick={doDelete} disabled={busy} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="delete-order-btn"><Trash2 size={16} className="mr-1" /> Delete</Button>}
        </div>}
      />
      {/* Flow progress — same visual pattern as Distributor > Retailer Orders detail */}
      <div className="flex items-center gap-2 mb-4 text-xs font-medium">
        {["placed", "fulfilled", "received"].map((st, i) => {
          const order_seq = { pending: 0, partially_fulfilled: 0, fulfilled: 1, ready_to_go: 1, received: 2 };
          const cur = order_seq[order.status] ?? 0;
          const label = ["Order Placed", "Fulfilled & Ready", "Received by Distributor"][i];
          const done = i <= cur;
          return (
            <React.Fragment key={st}>
              {i > 0 && <div className={`h-0.5 w-6 ${i <= cur ? "bg-emerald-500" : "bg-slate-200"}`} />}
              <span className={`px-2.5 py-1 rounded-full ${done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{label}</span>
            </React.Fragment>
          );
        })}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Ordered By</div><div className="mt-1 text-sm font-medium text-slate-900">{order.distributor_name}</div><div className="text-[11px] text-slate-400 mt-0.5">distributor</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Status</div><div className="mt-1"><span className={`text-sm px-2.5 py-1 rounded-full border ${statusPill(order.status)}`}>{order.status.replace(/_/g, " ")}</span></div></Card>
        <FulfilShortCard order={order} canFulfill={canFulfill} busy={busy} saveAll={saveAll} />
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Order Total</div><div className="mt-1 font-bold text-lg text-slate-900">{inr(order.total)}</div><div className="text-xs text-slate-500">Sub {inr(order.subtotal)} + GST {inr(order.gst_total)}{(order.trade_discount_total || 0) > 0 ? " - Trade Discount " + inr(order.trade_discount_total) : ""}</div></Card>
      </div>

      <Card className="mb-4">
        <div className="p-4 border-b border-slate-100 font-semibold flex items-center justify-between">
          <span>Line Items — set fulfillment quantity</span>
          {canFulfill && (
            <div className="flex gap-2">
              <Button size="sm" onClick={saveAll} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="po-save-all-btn">Save All</Button>
              <Button size="sm" variant="outline" onClick={openAddPoItem} disabled={busy} data-testid="po-add-item-btn"><Plus size={14} className="mr-1" /> Add Item</Button>
            </div>
          )}
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Price</TableHead><TableHead>Ordered</TableHead><TableHead>In Stock</TableHead><TableHead>Fulfilled</TableHead><TableHead>Line Total</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {order.items.map(it => (
              <TableRow key={it.product_id}>
                <TableCell><div className="font-medium">{it.product_name}</div><div className="text-xs font-mono text-slate-500">{it.sku_code}</div></TableCell>
                <TableCell>{inr(it.unit_price)}</TableCell>
                <TableCell>{Math.round(it.qty_boxes_ordered * (it.box_qty || 1))} pcs</TableCell>
                <TableCell>
                  <span className={`text-sm font-semibold ${((it.owner_stock_boxes ?? 0) < it.qty_boxes_ordered) ? "text-rose-600" : "text-emerald-600"}`} data-testid={`stock-${it.product_id}`}>
                    {(it.owner_stock_boxes ?? 0) < 0
                      ? `Backorder: ${Math.round(Math.abs(it.owner_stock_boxes) * (it.box_qty || 1))} pcs`
                      : `${Math.round((it.owner_stock_boxes ?? 0) * (it.box_qty || 1))} pcs`}
                  </span>
                </TableCell>
                <TableCell>
                  {canFulfill ? (
                    (() => {
                      const bq = it.box_qty || 1;
                      const totalPcs = Math.round(it.qty_boxes_fulfilled * bq);
                      const orderedPcs = Math.round(it.qty_boxes_ordered * bq);
                      return (
                        <div key={`${it.product_id}-${it.qty_boxes_fulfilled}`} className="flex flex-wrap items-center gap-2">
                          <Input type="number" min={0} step="1" defaultValue={Math.floor(totalPcs / bq)}
                            className="w-20" disabled={busy} data-testid={`fill-bx-${it.product_id}`} />
                          <span className="text-xs text-slate-500">bx</span>
                          <Input type="number" min={0} step="1" defaultValue={totalPcs % bq}
                            className="w-20" disabled={busy} data-testid={`fill-pcs-${it.product_id}`} />
                          <span className="text-xs text-slate-500">pcs</span>
                          
                          {/* GIVEALL_V2_BEGIN */}
                          {(it.lots || []).length <= 1 && (
                            <button type="button" disabled={busy} data-testid={`giveall-${it.product_id}`}
                              className="ml-3 rounded-md border border-emerald-600 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
                              onClick={() => {
                                const q = (t) => document.querySelector(`[data-testid="${t}"]`);
                                const setVal = (el, v) => { if (!el) return; Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set.call(el, v); el.dispatchEvent(new Event("input", { bubbles: true })); };
                                setVal(q(`fill-bx-${it.product_id}`), "0");
                                setVal(q(`fill-pcs-${it.product_id}`), String(Math.round((it.qty_boxes_ordered || 0) * bq)));
                              }}>
                              Give all {Math.round((it.qty_boxes_ordered || 0) * bq)}
                            </button>
                          )}
                          {(it.lots || []).length > 1 && (
                            <div style={{ flexBasis: "100%" }} className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid={`batch-panel-${it.product_id}`}>
                              <div className="mb-4 flex flex-wrap items-center gap-3">
                                <span className="text-base font-bold text-slate-800">Which batch to give from?</span>
                                <span className="text-sm text-slate-500">Leave blank = oldest batch first automatically.</span>
                                <span className="text-sm font-semibold text-emerald-700" data-testid={`batch-sum-${it.product_id}`}></span>
                              </div>
                              <div className="space-y-3">
                                {[...(it.lots || [])].sort((a, b) => a.batch_no - b.batch_no).map((l, idx) => {
                                  const stockPcs = Math.round((l.qty_boxes || 0) * bq);
                                  const ordPcs = Math.round((it.qty_boxes_ordered || 0) * bq);
                                  const tid = `batch-${it.product_id}-${l.batch_no}`;
                                  const setVal = (el, v) => { if (!el) return; Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set.call(el, v); el.dispatchEvent(new Event("input", { bubbles: true })); };
                                  const sync = () => {
                                    const s = (it.lots || []).reduce((a, x) => a + Number(document.querySelector(`[data-testid="batch-${it.product_id}-${x.batch_no}"]`)?.value || 0), 0);
                                    const el = document.querySelector(`[data-testid="batch-sum-${it.product_id}"]`);
                                    if (el) el.textContent = s > 0 ? `Will fulfil ${s} pcs from batches` : "";
                                    if (s > 0) {
                                      setVal(document.querySelector(`[data-testid="fill-pcs-${it.product_id}"]`), String(s));
                                      setVal(document.querySelector(`[data-testid="fill-bx-${it.product_id}"]`), "0");
                                    }
                                  };
                                  return (
                                    <div key={l.batch_no} className="flex flex-wrap items-center gap-4 rounded-lg border border-amber-200 bg-white p-3">
                                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">{idx + 1}</span>
                                      <div className="min-w-[200px] flex-1">
                                        <div className="text-base font-semibold text-slate-800">
                                          {l.label || `Batch ${l.batch_no}`}
                                          {idx === 0 && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">OLDEST</span>}
                                        </div>
                                        <div className="mt-1 text-sm text-slate-600">
                                          Price {inr(l.unit_price)} | Stock {stockPcs} pcs
                                          {stockPcs <= 0 && <span className="ml-1 font-semibold text-rose-600">(no stock)</span>}
                                        </div>
                                      </div>
                                      <Input type="number" min={0} step="1" placeholder="pcs" className="h-10 w-28 bg-white" disabled={busy} data-testid={tid} onInput={sync} />
                                      <button type="button" disabled={busy}
                                        className="rounded-md border border-amber-500 bg-white px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
                                        onClick={() => {
                                          (it.lots || []).forEach(x => setVal(document.querySelector(`[data-testid="batch-${it.product_id}-${x.batch_no}"]`), x.batch_no === l.batch_no ? String(ordPcs) : ""));
                                          sync();
                                        }}>
                                        Give all {ordPcs}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {/* GIVEALL_V2_END */}
                          {(it.allocations || []).length > 0 && (
                            <div style={{ flexBasis: "100%" }} className="text-xs text-slate-500">
                              Saved: {it.allocations.map(a => `${((it.lots || []).find(x => x.batch_no === a.batch_no)?.label) || ("Batch " + a.batch_no)}: ${Math.round(a.qty_boxes * bq)} pcs @ ${inr(a.unit_price)}`).join(" | ")}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div>
                      <span className="font-semibold">{Math.round(it.qty_boxes_fulfilled * (it.box_qty || 1))} pcs</span>
                      {(it.allocations || []).length > 0 && (
                        <div className="text-xs text-slate-500">{it.allocations.map(a => `${((it.lots || []).find(x => x.batch_no === a.batch_no)?.label) || ("Batch " + a.batch_no)}: ${Math.round(a.qty_boxes * (it.box_qty || 1))} pcs @ ${inr(a.unit_price)}`).join(" | ")}</div>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{inr(it.line_total)}</TableCell>
                <TableCell>
                  {canFulfill && order.items.length > 1 && (
                    <button onClick={() => removePoItem(it)} disabled={busy} className="p-1 hover:bg-rose-50 rounded" title="Remove item" data-testid={`po-remove-${it.product_id}`}>
                      <Trash2 size={14} className="text-rose-500" />
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Item to Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Product</Label>
              <Select value={addPid} onValueChange={setAddPid}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select a product" /></SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 sticky top-0 bg-white z-10">
                    <Input placeholder="Search product name or SKU…" value={addSearch} onChange={e => setAddSearch(e.target.value)} onKeyDown={e => e.stopPropagation()} className="h-8" />
                  </div>
                  {addProds
                    .filter(pr => {
                      const needle = addSearch.trim().toLowerCase();
                      if (!needle) return true;
                      return (pr.name || "").toLowerCase().includes(needle) || (pr.sku_code || "").toLowerCase().includes(needle);
                    })
                    .map(pr => (<SelectItem key={pr.id} value={pr.id}>{pr.name} ({pr.sku_code})</SelectItem>))}
                  {addProds.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No more products to add</div>}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Boxes</Label><Input type="number" step="1" min={0} value={addBoxes} onChange={e => setAddBoxes(e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={confirmAddPoItem} disabled={busy} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] text-white">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {order.ebill && (
        <Card className="mb-4 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Receipt size={18} className="text-[#a67c00]" /><div className="font-semibold">e-Bill</div>
              <span className="font-mono text-sm text-slate-600">{order.ebill.ebill_no}</span>
              <button onClick={openEditEbillNo} className="p-1 hover:bg-slate-100 rounded" title="Change e-Bill No." data-testid="edit-ebill-no-btn"><Edit size={12} className="text-slate-400" /></button>
              {order.ebill.status === "voided" && <span className="text-[10px] uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Voided</span>}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right"><div className="font-bold text-lg">{inr(order.ebill.total)}</div><div className="text-xs text-slate-500">{niceDate(order.ebill.created_at)}</div></div>
              {order.ebill.status !== "voided" && (
                <button
                  onClick={async () => {
                    const ok = window.confirm(`Void e-Bill ${order.ebill.ebill_no}?\n\nThis removes it from the ledger. Stock already moved will NOT be reversed.`);
                    if (!ok) return;
                    try { await dms.voidEbill(order.ebill.id); toast.success("e-Bill voided"); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Failed to void"); }
                  }}
                  className="p-1.5 hover:bg-red-50 rounded" title="Void e-Bill"
                  data-testid="void-ebill-btn"
                >
                  <Trash2 size={16} className="text-red-500" />
                </button>
              )}
            </div>
          </div>
          <div className="text-sm text-slate-600">Subtotal {inr(order.ebill.subtotal)} • GST {inr(order.ebill.gst_total)}{(order.ebill.trade_discount_total || 0) > 0 ? " • Trade Discount -" + inr(order.ebill.trade_discount_total) : ""} • {order.ebill.items.length} line items dispatched</div>
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
            <Truck size={16} className="text-slate-500" />
            <span className="text-sm text-slate-600 shrink-0">e-Way Bill No.</span>
            <Input value={ewayInput} onChange={e => setEwayInput(e.target.value)} placeholder="Enter e-Way Bill no. from Vyapar" className="max-w-xs" disabled={savingEway} data-testid="eway-bill-input" />
            <Button size="sm" variant="outline" onClick={saveEwayBill} disabled={savingEway || ewayInput.trim() === (order.ebill.eway_bill_no || "")} data-testid="save-eway-bill-btn">Save</Button>
            {order.ebill.eway_bill_no && <span className="text-xs text-emerald-600">Saved {order.ebill.eway_bill_date ? niceDate(order.ebill.eway_bill_date) : ""}</span>}
          </div>
        </Card>
      )}

      <Dialog open={editNoOpen} onOpenChange={setEditNoOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change e-Bill No.</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1.5">Prefix</div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setNoPrefix("")} className={`px-3 py-1.5 rounded-full text-sm border ${!noPrefix ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>None</button>
                {noKnownPrefixes.map(p => (
                  <button key={p} type="button" onClick={() => setNoPrefix(p)} className={`px-3 py-1.5 rounded-full text-sm border ${noPrefix === p ? "bg-rose-100 border-rose-300 text-rose-700 font-semibold" : "bg-white border-slate-200 text-slate-600"}`}>{p}</button>
                ))}
                {noAddingPrefix ? (
                  <div className="flex items-center gap-1">
                    <Input value={noNewPrefix} onChange={e => setNoNewPrefix(e.target.value)} placeholder="e.g. MAR" className="w-24 h-8 text-sm" autoFocus />
                    <Button size="sm" onClick={() => { const p = noNewPrefix.trim().toUpperCase(); if (p) { setNoPrefix(p); setNoKnownPrefixes(prev => prev.includes(p) ? prev : [...prev, p]); } setNoNewPrefix(""); setNoAddingPrefix(false); }}>Add</Button>
                  </div>
                ) : (
                  <button type="button" onClick={() => { setNoAddingPrefix(true); setNoNewPrefix(""); }} className="px-3 py-1.5 rounded-full text-sm border border-dashed border-slate-300 text-[#a67c00]">+ Add Prefix</button>
                )}
              </div>
            </div>
            <div><Label>Number</Label><Input value={noNumber} onChange={e => setNoNumber(e.target.value)} data-testid="ebill-no-number-input" /></div>
          </div>
          <DialogFooter><Button onClick={saveEbillNo} disabled={savingNo} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-ebill-no-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={poMetaOpen} onOpenChange={setPoMetaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit PO Number / Date</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>PO Number</Label>
              <Input value={poNo} onChange={e => setPoNo(e.target.value)} data-testid="po-meta-number-input" />
            </div>
            <div>
              <Label>PO Date</Label>
              <Input type="date" value={poDate} onChange={e => setPoDate(e.target.value)} data-testid="po-meta-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={savePoMeta} disabled={savingPoMeta} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-po-meta-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {order.ebill && (
        <Card className="mb-4">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2"><Paperclip size={16} /><div className="font-semibold">Supporting Documents</div></div>
          <div className="p-4">
            {(order.attachments || []).length > 0 && (
              <div className="space-y-2 mb-3">
                {order.attachments.map(a => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#a67c00] hover:underline"><Paperclip size={14} /> {a.name}</a>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Input placeholder="Document name" value={attachName} onChange={e => setAttachName(e.target.value)} />
              <Input placeholder="Paste public URL (image/PDF)" className="md:col-span-1" value={attachUrl} onChange={e => setAttachUrl(e.target.value)} />
              <Button onClick={uploadAttachment} variant="outline" data-testid="add-attachment-btn"><Paperclip size={14} className="mr-1" />Attach</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Owner — Inventory
// ============================================================================
function fmtBoxes(v) {
  const n = Number(v);
  if (!isFinite(n)) return "0";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function OwnerDistributorInventoryPage() {
  const [distId, setDistId] = useState("all");
  const [dists, setDists] = useState([]);
  const [inv, setInv] = useState({ data: [], total_boxes: 0, total_value: 0 });
  const [loading, setLoading] = useState(true);
  const [prods, setProds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", delta_boxes: 0, price_per_box: "", reason: "manual_adjust" });

  useEffect(() => { dms.listDistributors().then(d => setDists(d.data || [])); dms.listProducts().then(p => setProds(p.data || [])); }, []);
  const load = () => { setLoading(true); dms.ownerDistributorInventory(distId).then(setInv).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [distId]);

  const save = async () => {
    try {
      const payload = { ...form, distributor_id: distId, delta_boxes: Number(form.delta_boxes) || 0 };
      if (form.price_per_box === "" || form.price_per_box === null) delete payload.price_per_box;
      else payload.price_per_box = Number(form.price_per_box);
      await dms.ownerDistributorInvAdjust(payload);
      toast.success("Stock updated");
      setOpen(false);
      setForm({ product_id: "", delta_boxes: 0, price_per_box: "", reason: "manual_adjust" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Distributor Inventory"
        subtitle={`Total: ${fmtBoxes(inv.total_boxes || 0)} boxes \u00b7 Value ${inr(inv.total_value || 0)}`}
        action={
          <Button
            onClick={() => setOpen(true)}
            disabled={distId === "all"}
            title={distId === "all" ? "Select a specific distributor first" : ""}
            className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white"
            data-testid="dist-stock-adj-btn"
          ><Plus size={16} className="mr-1" /> Stock Adjustment</Button>
        }
      />
      <div className="mb-4 max-w-xs">
        <Label>Distributor</Label>
        <Select value={distId} onValueChange={setDistId}>
          <SelectTrigger data-testid="dist-inv-filter"><SelectValue placeholder="Select distributor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Distributors</SelectItem>
            {dists.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              {distId === "all" && <TableHead>Distributor</TableHead>}
              <TableHead>SKU</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Qty (Boxes)</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>No. of Pieces</TableHead>
              <TableHead>No. of Liters</TableHead>
              <TableHead>Rate</TableHead>
              <TableHead>GST</TableHead>
              <TableHead>Purchase Price</TableHead>
              <TableHead>Sales Price</TableHead>
              <TableHead>Margin</TableHead>
              <TableHead>Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(inv.data || []).map(r => (
              <TableRow key={`${r.distributor_id}-${r.product_id}`}>
                {distId === "all" && <TableCell className="font-medium">{r.distributor_name}</TableCell>}
                <TableCell className="font-mono text-xs">{r.sku_code}</TableCell>
                <TableCell>{r.product_name}</TableCell>
                <TableCell className="font-bold text-slate-900">{fmtBoxes(r.qty_boxes)}</TableCell>
                <TableCell>{r.unit}</TableCell>
                <TableCell>{r.no_of_pieces}</TableCell>
                <TableCell>{r.no_of_liters}</TableCell>
                <TableCell>{inr(r.rate)}</TableCell>
                <TableCell>{r.gst_rate_display}</TableCell>
                <TableCell>{inr(r.purchase_price)}</TableCell>
                <TableCell>{inr(r.sales_price)}</TableCell>
                <TableCell>{r.margin_pct}%</TableCell>
                <TableCell className="font-semibold">{inr(r.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && (inv.data || []).length === 0 && <div className="p-8 text-center text-sm text-slate-500">No inventory</div>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stock Adjustment \u2014 {dists.find(d => d.id === distId)?.name || ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Product *</Label>
              <Select value={form.product_id} onValueChange={v => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="dist-adj-prod-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Change in Boxes (+/-) *</Label><Input type="number" value={form.delta_boxes} onChange={e => setForm({ ...form, delta_boxes: e.target.value })} data-testid="dist-adj-qty" /></div>
            <div><Label>Reason</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="dist-save-adj-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Shared: Stock Movement History (Owner + Distributor "My Stock" pages)
// ============================================================================
// Balance text with correct sign (negative stock shows as -N Pcs)
function fmtLedgerBalance(r) {
  const neg = (r.balance_after_total_pieces || 0) < 0;
  const w = Math.abs(r.balance_after_whole || 0);
  const p = r.balance_after_pieces || 0;
  const parts = [];
  if (w > 0 || p === 0) parts.push(`${w} Box`);
  if (p > 0) parts.push(`${p} Pcs`);
  return (neg ? "−" : "") + parts.join(", ");
}

export function StockLedgerSection({ scope, distributorId }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", retailer_id: "all", reason: "all" });
  const [retailerOpts, setRetailerOpts] = useState([]);
  const [reasonOpts, setReasonOpts] = useState([]);
  const [sm, setSm] = useState(null);
  const [cardKey, setCardKey] = useState(null);

  const load = (f) => {
    const cur = f || filters;
    setLoading(true);
    const params = {};
    if (scope) params.scope = scope;
    if (distributorId) params.distributor_id = distributorId;
    if (cur.date_from) params.date_from = cur.date_from;
    if (cur.date_to) params.date_to = cur.date_to;
    if (cur.retailer_id && cur.retailer_id !== "all") params.retailer_id = cur.retailer_id;
    if (cur.reason && cur.reason !== "all") params.reason = cur.reason;
    dms.stockLedger(params).then(d => {
      const rs = d.rows || [];
      setRows(rs);
      setSummary(d.retailer_summary || []);
      setSm(d.summary || null);
      if (!params.retailer_id && !params.reason) {
        setRetailerOpts((d.retailer_summary || [])
          .map(x => ({ id: x.retailer_id, name: x.retailer_name }))
          .sort((x, y) => (x.name || "").localeCompare(y.name || "")));
        const seen = {};
        rs.forEach(r => { if (r.reason) seen[r.reason] = r.reason_label || r.reason; });
        setReasonOpts(Object.entries(seen).map(([id, label]) => ({ id, label })));
      }
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selCls = "h-9 rounded-md border border-slate-200 bg-white px-2 text-sm max-w-[220px]";
  const resetAll = () => {
    const nf = { date_from: "", date_to: "", retailer_id: "all", reason: "all" };
    setFilters(nf);
    load(nf);
  };

  return (
    <Card className="mt-4 overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold text-slate-900">Stock Movement History</div>
        <div className="flex gap-2 items-center flex-wrap">
          <select className={selCls} value={filters.retailer_id} onChange={e => setFilters({ ...filters, retailer_id: e.target.value })} data-testid="ledger-retailer-filter">
            <option value="all">All retailers</option>
            {retailerOpts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <select className={selCls} value={filters.reason} onChange={e => setFilters({ ...filters, reason: e.target.value })} data-testid="ledger-reason-filter">
            <option value="all">All movement types</option>
            {reasonOpts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            {filters.reason !== "all" && !reasonOpts.some(o => o.id === filters.reason) && <option value={filters.reason}>Selected group</option>}
          </select>
          <Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="w-auto" />
          <Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="w-auto" />
          <Button size="sm" variant="outline" onClick={() => load()}>Apply</Button>
          <Button size="sm" variant="outline" onClick={resetAll}>Reset</Button>
        </div>
      </div>
      {sm && (() => {
        const K = {
          opening: ["opening_balance"],
          purchase: ["primary_receive"],
          sold: ["secondary_dispatch", "direct_sale", "secondary_owner_edit"],
          retRet: ["sale_return_from_retailer", "secondary_return_approved"],
          retCo: ["primary_return_approved", "sale_return_to_company"],
          manual: ["manual_adjust", "Stock Movement", "Stock", "stock_in", "stock_correction", "stock_reconciliation_20260926", "stock_reconciliation_20260926b"],
        };
        const known = Object.values(K).flat();
        const g = (keys) => (sm.by_reason || []).filter(x => keys.includes(x.reason))
          .reduce((a, x) => ({ in: a.in + x.in_pcs, out: a.out + x.out_pcs, n: a.n + x.count }), { in: 0, out: 0, n: 0 });
        const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
        const sgn = (n) => (n >= 0 ? "+" : "−") + fmt(Math.abs(n));
        const net = (x) => x.in - x.out;
        const otherKeys = (sm.by_reason || []).map(x => x.reason).filter(r => !known.includes(r));
        const opn = g(K.opening), pur = g(K.purchase), sold = g(K.sold), rr = g(K.retRet), rc = g(K.retCo), man = g(K.manual), oth = g(otherKeys);
        const bills = sm.purchases || [];
        const billPcs = bills.reduce((a, b) => a + (b.pcs || 0), 0);
        const pc = sm.product_check || [];
        const period = (filters.date_from || filters.date_to) ? "selected dates" : "all time";
        const pick = (reasons) => {
          const nf = { ...filters, reason: (reasons && reasons.length) ? reasons.join(",") : "all", retailer_id: "all" };
          setFilters(nf);
          load(nf);
        };
        const titles = {
          openingStock: "Opening stock", opening: "Opening balance entries", purchase: "Purchase bills from company",
          sold: "Sold to retailers", retRet: "Returned by retailers", retCo: "Returned to company",
          manual: "Manual adjustments", other: "Other movements", closing: "Closing stock (ledger) check", current: "Current stock (live) check",
        };
        const card = (key, title, value, sub, tone, reasons) => (
          <button type="button" key={key}
            onClick={() => {
              setCardKey(key);
              if (key !== "purchase") pick(reasons);
              setTimeout(() => { const el = document.getElementById("card-detail"); if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, 300);
            }}
            className={`text-left rounded-xl border bg-white p-3 transition hover:shadow-sm ${cardKey === key ? "border-amber-400 ring-1 ring-amber-300" : "border-slate-200 hover:border-amber-300"}`}>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{title}</div>
            <div className={`text-xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
            {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
          </button>
        );

        const R = rows || [];
        const pcsOf = (r) => Number(r.delta_total_pieces || 0);
        const rowsTable = (list) => {
          const tin = list.reduce((a, r) => a + (pcsOf(r) > 0 ? pcsOf(r) : 0), 0);
          const tout = list.reduce((a, r) => a + (pcsOf(r) < 0 ? -pcsOf(r) : 0), 0);
          return (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500">
                  <th className="p-2">Date</th><th className="p-2">Product</th><th className="p-2">Type</th><th className="p-2">Retailer</th>
                  <th className="p-2">Reference</th><th className="p-2">By / Remark</th><th className="p-2 text-right">Pcs</th>
                </tr></thead>
                <tbody>
                  {list.map(r => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="p-2 whitespace-nowrap">{niceDate(r.date)}</td>
                      <td className="p-2">{r.product_name}</td>
                      <td className="p-2">{r.reason_label || r.reason}</td>
                      <td className="p-2">{r.retailer_name || "—"}</td>
                      <td className="p-2">{r.reference || "—"}</td>
                      <td className="p-2">{r.by_user || r.remark ? [r.by_user, r.remark].filter(Boolean).join(" · ") : "—"}</td>
                      <td className={`p-2 text-right font-semibold ${pcsOf(r) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{sgn(pcsOf(r))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 font-semibold">
                    <td className="p-2" colSpan={6}>Total ({list.length} entries): in +{fmt(tin)} / out −{fmt(tout)}</td>
                    <td className="p-2 text-right">{sgn(tin - tout)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        };

        let body = null;
        if (cardKey === "purchase") {
          body = bills.length === 0 ? <div className="text-xs text-slate-500">No bills in this period.</div> : bills.map(b => (
            <div key={b.order_no} className="mb-3 rounded-lg border border-slate-200">
              <div className="p-2 text-xs flex flex-wrap gap-x-4 gap-y-1 border-b border-slate-100 bg-slate-50">
                <span className="font-mono font-semibold">{b.order_no}</span>
                <span>Received {niceDate(b.received_at)}</span>
                <span>{fmt(b.pcs)} pcs</span>
                <span>{inr(b.total)}</span>
                <span className="text-slate-500">{b.in_ledger ? "In stock ledger as purchase" : (b.in_opening ? "Stock counted in opening balance entries" : "Not found in stock ledger")}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-500"><th className="p-2">Product</th><th className="p-2 text-right">Boxes</th><th className="p-2 text-right">Pcs</th><th className="p-2 text-right">Rate / pc (ex-GST)</th><th className="p-2 text-right">Amount (incl. GST)</th></tr></thead>
                  <tbody>
                    {(b.items || []).map((it, k) => (
                      <tr key={k} className="border-t border-slate-100">
                        <td className="p-2">{it.product_name}</td>
                        <td className="p-2 text-right">{it.boxes}</td>
                        <td className="p-2 text-right">{fmt(it.pcs)}</td>
                        <td className="p-2 text-right">{inr(it.rate_per_pc)}</td>
                        <td className="p-2 text-right">{inr(it.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ));
        } else if (cardKey === "openingStock") {
          body = <div className="text-xs text-slate-700">Stock before this period: <b>{fmt(sm.opening_pcs)} pcs</b>. {sm.opening_pcs === 0 ? "No stock movement exists before the first date, so the stock starts from the opening balance entries." : "This is the total of all ledger entries before the start date."}</div>;
        } else if (cardKey === "closing" || cardKey === "current") {
          body = (
            <div className="text-xs space-y-2">
              <div>Ledger total: <b>{fmt(sm.closing_pcs)} pcs</b> · Live stock (My Stock): <b>{fmt(sm.current_pcs)} pcs</b> · Difference: <b>{sgn(sm.mismatch_pcs)} pcs</b></div>
              {pc.length === 0 ? (
                <div className="text-emerald-700">Every product matches between ledger and live stock. Live stock by product adds up to {fmt(sm.live_sum_pcs)} pcs.</div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="text-amber-800 mb-1">These products are different in ledger and live stock:</div>
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500"><th className="p-2">Product</th><th className="p-2 text-right">Ledger pcs</th><th className="p-2 text-right">Live pcs</th><th className="p-2 text-right">Difference</th></tr></thead>
                    <tbody>
                      {pc.map((x, k) => (
                        <tr key={k} className="border-t border-slate-100">
                          <td className="p-2">{x.product_name}</td>
                          <td className="p-2 text-right">{fmt(x.ledger_pcs)}</td>
                          <td className="p-2 text-right">{fmt(x.live_pcs)}</td>
                          <td className={`p-2 text-right font-semibold ${x.diff < 0 ? "text-rose-600" : "text-emerald-600"}`}>{sgn(x.diff)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        } else if (cardKey) {
          const byRet = {};
          if (cardKey === "sold") {
            R.forEach(r => {
              const k = r.retailer_name || "—";
              byRet[k] = byRet[k] || { pcs: 0, val: 0, refs: new Set() };
              byRet[k].pcs += -pcsOf(r);
              byRet[k].val += (r.direction === "in" ? -1 : 1) * (Number(r.value) || 0);
              if (r.reference) byRet[k].refs.add(r.reference);
            });
          }
          const retList = Object.entries(byRet).sort((a, b) => b[1].pcs - a[1].pcs);
          body = loading ? <div className="text-xs text-slate-500">Loading…</div> : (R.length === 0 ? (
            <div className="text-xs text-slate-500">No entries in this period.</div>
          ) : (
            <div>
              {retList.length > 0 && (
                <div className="mb-3 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500"><th className="p-2">Retailer</th><th className="p-2">Orders / invoices</th><th className="p-2 text-right">Pcs sold</th><th className="p-2 text-right">Value (incl. GST)</th></tr></thead>
                    <tbody>
                      {retList.map(([name, v]) => (
                        <tr key={name} className="border-t border-slate-100">
                          <td className="p-2 font-medium">{name}</td>
                          <td className="p-2 text-slate-600">{[...v.refs].join(", ") || "—"}</td>
                          <td className="p-2 text-right font-semibold text-rose-600">{fmt(v.pcs)}</td>
                          <td className="p-2 text-right font-semibold">{inr(v.val)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {cardKey === "sold" ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-500"><th className="p-2">Date</th><th className="p-2">Product</th><th className="p-2">Retailer</th><th className="p-2">Reference</th><th className="p-2 text-right">Pcs</th><th className="p-2 text-right">Value (incl. GST)</th></tr></thead>
                    <tbody>
                      {R.map((r, i) => (
                        <tr key={r.id || i} className="border-t border-slate-100">
                          <td className="p-2 whitespace-nowrap">{niceDate(r.date)}</td>
                          <td className="p-2">{r.product_name}</td>
                          <td className="p-2">{r.retailer_name || "—"}</td>
                          <td className="p-2 font-mono">{r.reference || "—"}</td>
                          <td className="p-2 text-right font-semibold text-rose-600">{sgn(pcsOf(r))}</td>
                          <td className="p-2 text-right font-semibold">{r.value == null ? "—" : inr((r.direction === "in" ? -1 : 1) * r.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 font-semibold">
                        <td className="p-2" colSpan={4}>Total ({R.length} entries)</td>
                        <td className="p-2 text-right">{sgn(R.reduce((a, r) => a + pcsOf(r), 0))}</td>
                        <td className="p-2 text-right">{inr(R.reduce((a, r) => a + (r.value == null ? 0 : (r.direction === "in" ? -1 : 1) * r.value), 0))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : rowsTable(R)}
            </div>
          ));
        }

        return (
          <div className="p-4 border-b border-slate-100 bg-slate-50/60" data-testid="ledger-summary">
            <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Summary ({period}) · click any card to see its full details</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {card("openingStock", "Opening stock", `${fmt(sm.opening_pcs)} pcs`, "before this period", null, null)}
              {card("opening", "Opening balance entry", `${sgn(net(opn))} pcs`, `${opn.n} entries`, "text-emerald-600", K.opening)}
              {card("purchase", "Purchased from company", `+${fmt(billPcs)} pcs`, bills.length ? `${bills.length} bill${bills.length > 1 ? "s" : ""}: ${bills.map(b => b.order_no).join(", ")}${bills.some(b => b.in_opening) ? " · stock counted in opening entry" : ""}` : "no bills in this period", "text-emerald-600", null)}
              {card("sold", "Sold to retailers", `${sgn(net(sold))} pcs`, `${sm.retailers} retailers · ${sm.orders} orders · ${inr(sm.sold_value || 0)}`, "text-rose-600", K.sold)}
              {card("retRet", "Returned by retailers", `${sgn(net(rr))} pcs`, `${rr.n} entries`, "text-emerald-600", K.retRet)}
              {card("retCo", "Returned to company", `${sgn(net(rc))} pcs`, `${rc.n} entries`, "text-rose-600", K.retCo)}
              {card("manual", "Manual adjustments", `${sgn(net(man))} pcs`, `+${fmt(man.in)} in / −${fmt(man.out)} out · ${man.n} entries`, "text-amber-600", K.manual)}
              {card("other", "Other movements", `${sgn(net(oth))} pcs`, `${oth.n} entries`, null, otherKeys.length ? otherKeys : null)}
              {card("closing", "Closing stock (ledger)", `${fmt(sm.closing_pcs)} pcs`, "opening + all movements", null, null)}
              {card("current", "Current stock (live)", `${fmt(sm.current_pcs)} pcs`, "as per My Stock", null, null)}
            </div>
            <div className="mt-3 text-xs rounded-lg border border-slate-200 bg-white p-2 text-slate-700">
              Opening {fmt(sm.opening_pcs)} {sgn(net(opn))} (opening entry) {sgn(net(pur))} (purchase entries) {sgn(net(sold))} (sold) {sgn(net(rr))} (retailer returns) {sgn(net(rc))} (company returns) {sgn(net(man))} (manual) {sgn(net(oth))} (other) = <b>{fmt(sm.closing_pcs)} pcs</b>
            </div>
            {sm.mismatch_pcs !== 0 && !filters.date_to && (
              <div className="mt-3 text-xs rounded-lg border border-amber-300 bg-amber-50 text-amber-800 p-2">
                Mismatch: ledger says {fmt(sm.closing_pcs)} pcs, but live stock is {fmt(sm.current_pcs)} pcs ({sgn(sm.mismatch_pcs)} pcs). Click "Closing stock" or "Current stock" to see which product.
              </div>
            )}
            {cardKey && (
              <div id="card-detail" className="mt-3 rounded-xl border border-amber-300 bg-white p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-900">{titles[cardKey]}</div>
                  <button type="button" className="text-xs text-slate-500 hover:text-slate-900" onClick={() => setCardKey(null)}>Close</button>
                </div>
                {body}
              </div>
            )}
            <details className="mt-3">
              <summary className="text-xs text-slate-600 cursor-pointer">Movement type wise detail</summary>
              <table className="w-full text-xs mt-2">
                <thead><tr className="text-left text-slate-500"><th className="py-1">Type</th><th className="py-1 text-right">Entries</th><th className="py-1 text-right">In (pcs)</th><th className="py-1 text-right">Out (pcs)</th></tr></thead>
                <tbody>
                  {(sm.by_reason || []).map(x => (
                    <tr key={x.reason} className="border-t border-slate-100">
                      <td className="py-1">{x.label}</td>
                      <td className="py-1 text-right">{x.count}</td>
                      <td className="py-1 text-right text-emerald-600">{x.in_pcs ? `+${fmt(x.in_pcs)}` : "0"}</td>
                      <td className="py-1 text-right text-rose-600">{x.out_pcs ? `−${fmt(x.out_pcs)}` : "0"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        );
      })()}
      {summary.length > 0 && (
        <div className="p-4 border-b border-slate-100 bg-slate-50/60">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-xs font-semibold uppercase text-slate-500">Retailer-wise stock out (click to filter)</div>
            <div className="text-sm font-semibold text-slate-900" data-testid="sold-total">
              Total: {summary.reduce((a, x) => a + (x.pieces_out || 0), 0)} pcs · {inr(summary.reduce((a, x) => a + (x.value_out || 0), 0))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.map(sm => (
              <button
                key={sm.retailer_id}
                type="button"
                onClick={() => { const nf = { ...filters, retailer_id: sm.retailer_id }; setFilters(nf); load(nf); }}
                className={`text-xs px-3 py-1.5 rounded-full border ${filters.retailer_id === sm.retailer_id ? "bg-amber-50 border-amber-300" : "bg-white border-slate-200 hover:border-slate-300"}`}
              >
                <span className="font-medium text-slate-900">{sm.retailer_name}</span>
                <span className="text-slate-500"> · {sm.orders} order{sm.orders > 1 ? "s" : ""} · {sm.pieces_out} pcs · {inr(sm.value_out || 0)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-slate-500 text-xs uppercase">
            <th className="p-3">Date</th>
            <th className="p-3">Product</th>
            <th className="p-3">Movement</th>
            <th className="p-3 text-right">Boxes</th>
            <th className="p-3 text-right">Pcs</th>
            <th className="p-3 text-right">Value (incl. GST)</th>
            <th className="p-3">Retailer</th>
            <th className="p-3">Reference</th>
            <th className="p-3">By / Remark</th>
            <th className="p-3 text-right">Balance After</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const sign = r.direction === "in" ? "+" : "−";
            return (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="p-3 text-xs text-slate-500">{niceDate(r.date)}</td>
                <td className="p-3">
                  <div className="font-medium text-slate-900">{r.product_name}</div>
                  {r.sku_code && <div className="font-mono text-[10px] text-slate-500">{r.sku_code}</div>}
                </td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full border ${r.direction === "in" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                    {r.reason_label || r.reason}
                  </span>
                </td>
                <td className={`p-3 text-right font-semibold ${r.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>
                  {r.delta_boxes_whole > 0 ? `${sign}${r.delta_boxes_whole}` : "0"}
                </td>
                <td className={`p-3 text-right font-semibold ${r.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>
                  {r.delta_pieces > 0 ? `${sign}${r.delta_pieces}` : "0"}
                </td>
                <td className="p-3 text-right font-semibold text-slate-900">{r.value == null ? "—" : inr((r.direction === "in" ? -1 : 1) * r.value)}</td>
                <td className="p-3 text-xs text-slate-700">{r.retailer_name || "—"}</td>
                <td className="p-3 text-xs text-slate-500">{r.reference || "—"}</td>
                <td className="p-3 text-xs text-slate-600">{r.by_user || r.remark ? (<div>{r.by_user && <div className="font-medium">{r.by_user}</div>}{r.remark && <div className="text-slate-500">{r.remark}</div>}</div>) : "—"}</td>
                <td className="p-3 text-right font-semibold text-slate-900">
                  {r.balance_after_boxes != null ? (
                    <div>
                      <div className={(r.balance_after_total_pieces || 0) < 0 ? "text-rose-600" : ""}>{fmtLedgerBalance(r)}</div>
                      <div className="text-[10px] text-slate-400 font-normal">Total stock: {r.total_stock_after_pieces} pcs • {inr(r.total_stock_after_value)}</div>
                    </div>
                  ) : "—"}
                </td>
              </tr>
            );
          })}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={10} className="p-6 text-center text-slate-400">No stock movements found</td></tr>
          )}
        </tbody>
        {rows.some(r => r.value != null) && (
          <tfoot>
            <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
              <td className="p-3" colSpan={5}>Total retailer sales ({rows.filter(r => r.value != null).length} entries)</td>
              <td className="p-3 text-right" data-testid="table-sold-total">{inr(rows.reduce((a, r) => a + (r.value == null ? 0 : (r.direction === "in" ? -1 : 1) * r.value), 0))}</td>
              <td className="p-3" colSpan={4}></td>
            </tr>
          </tfoot>
        )}
      </table>
    </Card>
  );
}

export function OwnerInventoryPage() {
  const [inv, setInv] = useState({ data: [], total_value: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", delta_boxes: 0, price_per_box: "", batch_no: "", reason: "stock_in" });
  const [prods, setProds] = useState([]);

  const load = () => Promise.all([dms.ownerInventory(), dms.listProducts()]).then(([i, p]) => { setInv(i); setProds(p.data); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, delta_boxes: Number(form.delta_boxes) || 0 };
      if (form.price_per_box === "" || form.price_per_box === null) delete payload.price_per_box;
      else payload.price_per_box = Number(form.price_per_box);
      if (form.batch_no === "" || form.batch_no === null || form.batch_no === undefined) delete payload.batch_no;
      else payload.batch_no = Number(form.batch_no);
      await dms.ownerInvAdjust(payload);
      toast.success("Stock updated");
      setOpen(false);
      setForm({ product_id: "", delta_boxes: 0, price_per_box: "", batch_no: "", reason: "stock_in" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Owner Inventory"
        subtitle={`Total value: ${inr(inv.total_value)}`}
        action={<Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="stock-in-btn"><Plus size={16} className="mr-1" /> Stock Adjustment</Button>}
      />
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Product</TableHead><TableHead>Boxes</TableHead><TableHead>Pcs</TableHead><TableHead>Bottles/Box</TableHead><TableHead>Price / Box</TableHead><TableHead>Total Value</TableHead></TableRow></TableHeader>
          <TableBody>
            {inv.data.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.sku_code}</TableCell>
                <TableCell className="font-medium">{r.product_name}</TableCell>
                <TableCell className="font-bold text-slate-900">{r.qty_boxes}</TableCell>
                <TableCell className="font-semibold text-slate-700">{Math.round((r.qty_boxes || 0) * (r.box_qty || 1))}</TableCell>
                <TableCell>{r.box_qty}</TableCell>
                <TableCell>{inr(r.unit_price)}</TableCell>
                <TableCell className="font-semibold">{inr(r.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {inv.data.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No inventory</div>}
      </Card>

      <StockLedgerSection scope="owner" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Stock Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Product *</Label>
              <Select value={form.product_id} onValueChange={v => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="adj-prod-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                <SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Change in Boxes (+/-) *</Label><Input type="number" value={form.delta_boxes} onChange={e => setForm({ ...form, delta_boxes: e.target.value })} data-testid="adj-qty" /></div>
            <div><Label>Batch No (optional)</Label><Input type="number" min={0} step="1" placeholder="blank = automatic" value={form.batch_no} onChange={e => setForm({ ...form, batch_no: e.target.value })} data-testid="adj-batch" /></div>
            <div><Label>Price / Box of this batch (optional — blank = keep current)</Label><Input type="number" value={form.price_per_box} onChange={e => setForm({ ...form, price_per_box: e.target.value })} data-testid="adj-price" /></div>
            <div><Label>Reason</Label><Input value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-adj-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Owner — Reward (Points) Wallets — scanned vs redeemed per retailer
// ============================================================================
function RewardWalletHistoryModal({ open, onOpenChange, retailerId, retailerName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open && retailerId) {
      setLoading(true);
      dms.cpnRewardWalletHistory(retailerId)
        .then(r => setRows(r.data || []))
        .finally(() => setLoading(false));
    }
  }, [open, retailerId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{retailerName} — Reward Wallet History</DialogTitle></DialogHeader>
        {loading ? (
          <div className="text-sm text-slate-500 py-6 text-center">Loading...</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead className="text-right">Points</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs text-slate-500">{niceDate(r.at)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${r.kind === "credit_coupon" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                        {r.kind === "credit_coupon" ? "Scanned" : "Redeemed"}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">{r.created_by_name || "—"}</TableCell>
                    <TableCell className={`text-right font-semibold ${r.kind === "credit_coupon" ? "text-emerald-700" : "text-rose-700"}`}>
                      {r.kind === "credit_coupon" ? "+" : "-"}{Math.abs(r.amount)}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-500 py-6">No transactions yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RewardWalletsPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_scanned: 0, total_redeemed: 0, total_balance: 0 });
  const [historyFor, setHistoryFor] = useState(null); // { retailer_id, retailer_name }
  const load = () => dms.cpnReportsRewardWalletsDetail().then(r => {
    setRows(r.data || []);
    setTotals({ total_scanned: r.total_scanned, total_redeemed: r.total_redeemed, total_balance: r.total_balance });
  });
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Reward Wallets" subtitle="Points scanned vs redeemed, per retailer" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Scanned</div>
          <div className="text-xl font-bold text-emerald-700 mt-1">{totals.total_scanned}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Redeemed</div>
          <div className="text-xl font-bold text-rose-700 mt-1">{totals.total_redeemed}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Outstanding Balance</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{totals.total_balance}</div>
        </Card>
      </div>
      <Card>
        <div className="p-4 border-b border-slate-100 font-semibold">Retailer-wise Reward Wallets</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Retailer</TableHead>
              <TableHead>Distributor</TableHead>
              <TableHead className="text-right">Scanned</TableHead>
              <TableHead className="text-right">Redeemed</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.retailer_id}>
                <TableCell className="font-medium">{r.retailer_name}</TableCell>
                <TableCell className="text-xs text-slate-500">{r.distributor_name}</TableCell>
                <TableCell className="text-right text-emerald-700 font-semibold">{r.scanned}</TableCell>
                <TableCell className="text-right text-rose-700 font-semibold">{r.redeemed}</TableCell>
                <TableCell className="text-right font-bold">{r.balance}</TableCell>
                <TableCell>
                  <button
                    onClick={() => setHistoryFor({ retailer_id: r.retailer_id, retailer_name: r.retailer_name })}
                    className="text-xs text-[#a67c00] hover:underline"
                    data-testid={`view-history-${r.retailer_id}`}
                  >
                    View history
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-6">No reward wallets yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <RewardWalletHistoryModal
        open={!!historyFor}
        onOpenChange={(o) => !o && setHistoryFor(null)}
        retailerId={historyFor?.retailer_id}
        retailerName={historyFor?.retailer_name}
      />
    </div>
  );
}

// ============================================================================
// Owner — Primary Ledger (also used by owner accountant + distributor)
// ============================================================================
export function PrimaryLedgerPage() {
  const { user } = useAuth();
  const isDistributor = user?.role === "distributor" || user?.role === "distributor_accountant";
  const [data, setData] = useState({ entries: [], summary: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ distributor_id: "", amount: "", method: "bank_transfer", description: "" });
  const [dists, setDists] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [retDetail, setRetDetail] = useState(null);
  const load = () => Promise.all([dms.primaryLedger(), dms.listDistributors()]).then(([l, d]) => { setData(l); setDists(d.data); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (form.method === "bank_transfer" && !(form.reference_no || "").trim()) {
      toast.error("UTR / transaction reference number is required for Bank Transfer");
      return;
    }
    try {
      if (isDistributor) {
        await dms.submitPrimaryPayment({ amount: Number(form.amount), method: form.method, description: form.description, reference_no: form.reference_no });
        toast.success("Payment submitted — awaiting confirmation from the company");
      } else {
        await dms.recordPrimaryPayment({ ...form, amount: Number(form.amount) });
        toast.success("Payment recorded");
      }
      setOpen(false); setForm({ distributor_id: "", amount: "", method: "bank_transfer", description: "" }); load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const doConfirm = async (entryId) => {
    setConfirming(entryId);
    try { await dms.confirmPrimaryPayment(entryId); toast.success("Payment confirmed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setConfirming(null); }
  };

  return (
    <div>
      <LedgerEntryDialog entry={retDetail} onClose={() => setRetDetail(null)} debtorView={isDistributor} />
      <PageHeader
        title="Primary Sales Ledger"
        subtitle={isDistributor ? "Your transactions with the company" : "Owner ↔ Distributor transactions"}
        action={<Button onClick={() => setOpen(true)} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="record-payment-btn"><IndianRupee size={16} className="mr-1" /> {isDistributor ? "Submit Payment" : "Record Payment"}</Button>}
      />
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Outstanding by Distributor</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.summary.map(s => (
            <Card key={s.distributor_id} className="p-4">
              <div className="text-sm font-semibold">{s.distributor_name}</div>
              <div className="mt-2 grid grid-cols-4 gap-2 text-center">
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Billed</div><div className="font-semibold text-slate-900 text-sm">{inr(s.billed)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Returns</div><div className="font-semibold text-amber-700 text-sm">{inr(s.returns || 0)}</div></div><div><div className="text-[10px] uppercase tracking-wider text-slate-500">Paid</div><div className="font-semibold text-emerald-700 text-sm">{inr(s.paid)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Due</div><div className="font-bold text-rose-700 text-sm">{inr(s.outstanding)}</div></div>
              </div>
            </Card>
          ))}
          {data.summary.length === 0 && <Card className="p-6 col-span-full text-center text-sm text-slate-500">No transactions yet</Card>}
        </div>
      </div>
      <Card>
        <div className="p-4 border-b border-slate-100 font-semibold">Ledger Entries</div>
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Distributor</TableHead><TableHead>Reference</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead>{!isDistributor && <TableHead></TableHead>}</TableRow></TableHeader>
          <TableBody>
            {data.entries.map(e => {
              const dName = dists.find(d => d.id === e.distributor_id)?.name || e.distributor_id;
              const status = e.status || "confirmed";
              return (
                <TableRow key={e.id} onClick={() => setRetDetail(e)} className="cursor-pointer hover:bg-slate-50">
                  <TableCell className="text-xs text-slate-500">{niceDate(e.at)}</TableCell>
                  <TableCell className="font-medium">{dName}</TableCell>
                  <TableCell className="font-mono text-xs">{e.reference_no}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded ${e.kind === "invoice" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>{e.kind}</span></TableCell>
                  <TableCell>
                    {e.kind === "payment" ? (
                      status === "pending"
                        ? <span className="text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700">Pending</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">Confirmed</span>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className={`text-right font-semibold ${e.kind === "invoice" ? "text-rose-700" : "text-emerald-700"}`}>{(e.kind === "invoice") !== isDistributor ? "+" : "-"}{inr(e.amount)}</TableCell>
                  {!isDistributor && (
                    <TableCell>
                      {e.kind === "payment" && status === "pending" && (
                        <Button size="sm" variant="outline" disabled={confirming === e.id} onClick={(ev) => { ev.stopPropagation(); doConfirm(e.id); }} data-testid={`confirm-payment-${e.id}`}>
                          {confirming === e.id ? "Confirming…" : "Confirm"}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {data.entries.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No entries yet</div>}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isDistributor ? "Submit Payment" : "Record Payment from Distributor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!isDistributor && (
              <div><Label>Distributor *</Label>
                <Select value={form.distributor_id} onValueChange={v => setForm({ ...form, distributor_id: v })}>
                  <SelectTrigger data-testid="pay-dist"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{dists.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Amount (₹) *</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="pay-amt" /></div>
            <div><Label>Method</Label>
              <Select value={form.method} onValueChange={v => setForm({ ...form, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.method === "bank_transfer" && (
              <div><Label>UTR / Transaction Reference No. *</Label>
                <Input value={form.reference_no || ""} onChange={e => setForm({ ...form, reference_no: e.target.value })} placeholder="Enter UTR number" data-testid="pay-utr" />
              </div>
            )}
            <div><Label>Note</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            {isDistributor && <p className="text-xs text-slate-500">This payment will stay pending until the company confirms it.</p>}
          </div>
          <DialogFooter><Button onClick={save} className="bg-gradient-to-r from-[#c9a227] to-[#a67c00] hover:from-[#b8931f] hover:to-[#8a6600] text-white" data-testid="save-payment-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================================
// Excel Import / Export for Products (small toolbar buttons)
// ============================================================================
import { FileDown, FileUp } from "lucide-react";
import { LedgerEntryDialog } from "./ReturnDetail";
// ============================================================================
// Owner — Primary Returns (approve/reject returns raised by distributors)
// ============================================================================
export function OwnerReturnsPage() {
  const [returns, setReturns] = useState([]);
  const [filter, setFilter] = useState("requested");
  const [busy, setBusy] = useState(false);

  const load = () => dms.listReturns(filter === "all" ? {} : { status: filter }).then(d => setReturns(d.data || []));
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    if (!window.confirm("Approve this return? Stock will be adjusted.")) return;
    setBusy(true);
    try { await dms.approveReturn(id); toast.success("Return approved"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional):") || "";
    setBusy(true);
    try { await dms.rejectReturn(id, reason); toast.success("Return rejected"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const statusPillReturn = (st) => st === "requested" ? "bg-amber-50 text-amber-700 border-amber-200" : st === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200";

  return (
    <div>
      <PageHeader title="Returns" subtitle="Primary returns raised by distributors" />
      <div className="flex gap-2 mb-4">
        {["requested", "approved", "rejected", "all"].map(s => (
          <Button key={s} size="sm" variant="outline" onClick={() => setFilter(s)} className={filter === s ? "bg-[#a67c00] text-white border-[#a67c00] hover:bg-[#8a6600]" : ""}>
            {s[0].toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>
      {returns.length === 0 ? (
        <EmptyState icon={RotateCcw} title="No returns" description="No return requests in this view." />
      ) : (
        <Card>
          <Table>
            <TableHeader><TableRow><TableHead>Return #</TableHead><TableHead>Order</TableHead><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {returns.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono">{r.return_no}</TableCell>
                  <TableCell>{r.order_no || "-"}</TableCell>
                  <TableCell>{r.items.length} item(s)</TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-full border ${statusPillReturn(r.status)}`}>{r.status}</span></TableCell>
                  <TableCell>{niceDate(r.created_at)}</TableCell>
                  <TableCell>
                    {r.status === "requested" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approve(r.id)} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white">Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => reject(r.id)} disabled={busy} className="text-red-600 border-red-200">Reject</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

const PRICE_FIELDS = [
  ["material_description", "Material Description"], ["grade_specs", "Grade / Specs"], ["pack_size", "Pack Size"],
  ["mrp", "MRP"], ["dlp", "DLP"], ["price_with_gst", "Price with GST"], ["price_without_gst", "Price without GST"],
  ["gst_rate", "GST Rate"], ["piece_per_box", "Piece per Box"], ["price_per_box", "Price per Box"],
  ["liter_per_box", "Liter per Box"], ["distributor_margin_pct", "Distributor Margin %"], ["cash_coupon", "Cash Coupon"],
  ["foc_benefits", "FOC Benefits"], ["monthly_gift", "Monthly Gift"], ["trade_discount", "Trade Discount"],
];
export function ExcelButtons({ onImported }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const doExport = async () => {
    setBusy(true);
    try { await dms.exportProducts(); toast.success("Products exported"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setBusy(false); }
  };
  const doTemplate = async () => {
    setBusy(true);
    try { await dms.importTemplate(); toast.success("Template downloaded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Template download failed"); }
    finally { setBusy(false); }
  };
  const [pv, setPv] = useState(null);
  const [pvFile, setPvFile] = useState(null);
  const [cmap, setCmap] = useState({});
  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      // 1) Preview — parse only, no DB writes
      const p = await dms.importPreview(f);
      if (!p.ok || p.product_count === 0) {
        toast.error((p.warnings?.[0]) || "No products detected in this file. Check the format / use Template.");
        return;
      }
      setPv(p); setPvFile(f);
      const inv = {}; Object.entries(p.auto_colmap || {}).forEach(([k, i]) => { inv[i] = k; }); setCmap(inv);
    } catch (err) { toast.error(err?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const applyImport = async () => {
    if (!pvFile) return;
    setBusy(true);
    try {
      const map = {}; const dup = [];
      Object.entries(cmap).forEach(([ci, fk]) => { if (!fk) return; if (map[fk] !== undefined) dup.push(fk); map[fk] = Number(ci); });
      if (dup.length) { toast.error("One field is mapped to two columns: " + dup.join(", ")); return; }
      if (map.material_description === undefined || (map.mrp === undefined && map.dlp === undefined && map.price_without_gst === undefined)) {
        toast.error("Material Description and MRP/DLP mapping is required"); return;
      }
      const r = await dms.importPriceCircular(pvFile, undefined, JSON.stringify(map));
      toast.success(`Imported ${r.products_parsed} products (+${r.created} new, ${r.updated} updated) across ${r.categories} categories. Price Circular Batch ${r.circular_batch_no} created.`);
      if (r.warnings?.length) toast.warning(r.warnings.slice(0, 2).join("\n"));
      setPv(null); setPvFile(null);
      onImported?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  const closePv = () => { if (!busy) { setPv(null); setPvFile(null); } };
  const pvCols = pv ? Math.max((pv.headers || []).length, ...((pv.rows || []).map(r => (r.cells || []).length)), 1) : 0;
  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden" onChange={doImport} data-testid="import-file-input" />
      <Button variant="outline" onClick={doTemplate} disabled={busy} data-testid="template-products-btn"><FileDown size={14} className="mr-1" /> Template</Button>
      <Button variant="outline" onClick={doExport} disabled={busy} data-testid="export-products-btn"><FileDown size={14} className="mr-1" /> Export</Button>
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="import-products-btn"><FileUp size={14} className="mr-1" /> {busy ? "Working…" : "Import"}</Button>
      <Dialog open={!!pv} onOpenChange={(o) => { if (!o) closePv(); }}>
        <DialogContent className="max-w-[96vw] w-[96vw] max-h-[92vh] flex flex-col" data-testid="import-preview-dialog">
          <DialogHeader>
            <DialogTitle>Price List Preview — {pvFile?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-slate-600">
            Products: <b>{pv?.product_count}</b> · Categories: <b>{pv?.category_count}</b> · Columns: <b>{pvCols}</b>
            {pv?.rows_total > (pv?.rows || []).length && <span> · Showing first {(pv?.rows || []).length} of {pv?.rows_total} rows</span>}
            {(pv?.warnings || []).length > 0 && <div className="text-amber-700 mt-1">{pv.warnings.join(" | ")}</div>}
            <div className="mt-1">Tapping Confirm & Apply creates a new dated Price Circular batch.</div>
          </div>
          <div className="overflow-auto border rounded-md flex-1" style={{ maxHeight: "60vh" }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">#</TableHead>
                  {Array.from({ length: pvCols }).map((_, ci) => (
                    <TableHead key={ci} className="whitespace-nowrap align-top">
                      <div>{(pv?.headers || [])[ci] || `Col ${ci + 1}`}</div>
                      <select value={cmap[ci] || ""} onChange={(e) => setCmap(m => ({ ...m, [ci]: e.target.value }))}
                        className="mt-1 text-xs border rounded px-1 py-0.5 bg-white font-normal" data-testid={`map-col-${ci}`}>
                        <option value="">— Ignore —</option>
                        {PRICE_FIELDS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                      </select>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(pv?.rows || []).map((r, ri) => r.type === "category" ? (
                  <TableRow key={ri} className="bg-slate-100">
                    <TableCell colSpan={pvCols + 1} className="font-semibold">{(r.cells || []).find(c => c) || ""}</TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={ri}>
                    <TableCell className="text-slate-400">{ri + 1}</TableCell>
                    {Array.from({ length: pvCols }).map((_, ci) => (
                      <TableCell key={ci} className="whitespace-nowrap">{(r.cells || [])[ci] ?? ""}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePv} disabled={busy} data-testid="import-preview-cancel">Cancel</Button>
            <Button onClick={applyImport} disabled={busy} data-testid="import-preview-apply">{busy ? "Applying…" : "Confirm & Apply"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// ── Owner: assign price lists to a distributor ──
function DistPriceListsTab({ id }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [dflt, setDflt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    dms.ownerDistPriceLists(id).then(d => {
      setData(d); setSel(d.allowed_circular_ids || []); setDflt(d.default_circular_id || "");
    }).catch(() => toast.error("Failed to load price lists"));
  }, [id]);

  const toggle = (cid) => {
    const n = sel.includes(cid) ? sel.filter(x => x !== cid) : [...sel, cid];
    setSel(n);
    if (!n.includes(dflt)) setDflt(n[0] || "");
  };
  const save = async () => {
    setBusy(true);
    try {
      await dms.ownerSetDistPriceLists(id, { allowed_circular_ids: sel, default_circular_id: dflt || null });
      toast.success("Price lists saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  if (!data) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  return (
    <Card>
      <div className="p-4 border-b border-slate-100 text-sm text-slate-600">
        Select which month's price lists this distributor can see. The distributor can set any of these as default.
        If nothing is selected, the distributor keeps using the normal current price.
      </div>
      <div className="divide-y divide-slate-100">
        {data.lists.map(l => (
          <label key={l.id} className="flex items-center gap-3 p-3 cursor-pointer" data-testid={`pl-${l.id}`}>
            <input type="checkbox" checked={sel.includes(l.id)} onChange={() => toggle(l.id)} />
            <div className="flex-1">
              <div className="text-sm font-medium text-slate-900">{l.title || l.batch_label || l.id}</div>
              <div className="text-xs text-slate-500">Effective {l.effective_date || "—"}{l.batch_no < 6 ? ` · Batch ${l.batch_no}` : ""}</div>
            </div>
            {sel.includes(l.id) && (
              <label className="text-xs text-slate-600 flex items-center gap-1">
                <input type="radio" name="pl-default" checked={dflt === l.id} onChange={() => setDflt(l.id)} /> Default
              </label>
            )}
          </label>
        ))}
      </div>
      <div className="p-4"><Button onClick={save} disabled={busy} data-testid="pl-save">Save</Button></div>
    </Card>
  );
}

/* FULFILCARD_V1_BEGIN */
function FulfilShortCard({ order, canFulfill, busy, saveAll }) {
  const [open, setOpen] = React.useState(false);
  const rows = (order.items || []).map((it) => {
    const bq = it.box_qty || 1;
    const ordP = Math.round((it.qty_boxes_ordered || 0) * bq);
    const fulP = Math.round((it.qty_boxes_fulfilled || 0) * bq);
    return { it, bq, ordP, fulP, short: ordP - fulP };
  }).filter((r) => r.short > 0);

  const q = (t) => document.querySelector(`[data-testid="${t}"]`);
  const setVal = (el, v) => {
    if (!el) return;
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set.call(el, v);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };
  const fillLine = (r) => {
    setVal(q(`fill-bx-${r.it.product_id}`), "0");
    setVal(q(`fill-pcs-${r.it.product_id}`), String(r.ordP));
  };
  const giveOne = (r) => { fillLine(r); setOpen(false); };
  const fulfilAll = () => {
    rows.forEach(fillLine);
    setOpen(false);
    setTimeout(() => { saveAll(); }, 50);
  };

  return (
    <>
      <div onClick={() => setOpen(true)} style={{ cursor: "pointer" }} data-testid="fulfil-card">
        <Card className="p-4 hover:shadow-md transition-shadow">
          <div className="text-xs text-slate-500 uppercase tracking-wider">Fulfillment <span className="normal-case text-[11px] text-slate-400">(click for details)</span></div>
          <div className="mt-1 flex items-center gap-3">
            <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-[#faf6e6]0 transition-all" style={{ width: `${order.fulfillment_pct}%` }} /></div>
            <span className="font-bold text-slate-900">{order.fulfillment_pct}%</span>
          </div>
        </Card>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)} data-testid="fulfil-popup">
          <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div className="text-lg font-bold text-slate-900">{rows.length ? "Not fully fulfilled" : "Fulfillment"}</div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-md border border-slate-300 px-3 py-1 text-sm">Close</button>
            </div>
            {rows.length === 0 ? (
              <div className="rounded-lg bg-emerald-50 p-4 text-emerald-700 font-semibold">All lines fulfilled 100%</div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase text-slate-500">
                      <th className="py-2">Product</th><th>Ordered</th><th>Fulfilled</th><th>Short by</th>{canFulfill && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.it.product_id} className="border-b">
                        <td className="py-2"><div className="font-medium">{r.it.product_name}</div><div className="text-xs font-mono text-slate-500">{r.it.sku_code}</div></td>
                        <td>{r.ordP} pcs</td>
                        <td>{r.fulP} pcs</td>
                        <td className="font-semibold text-rose-600">{r.short} pcs</td>
                        {canFulfill && (
                          <td className="text-right">
                            <button type="button" disabled={busy} onClick={() => giveOne(r)} data-testid={`give-rem-${r.it.product_id}`}
                              className="rounded-md border border-emerald-600 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
                              Give remaining {r.short}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {canFulfill && (
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="text-xs text-slate-500">"Give remaining" fills that row. Press Save All to save. "Fulfil all pending" fills and saves everything (works with minus stock).</span>
                    <button type="button" disabled={busy} onClick={fulfilAll} data-testid="fulfil-all-pending"
                      className="rounded-md bg-gradient-to-r from-[#c9a227] to-[#a67c00] px-4 py-2 text-sm font-semibold text-white">
                      Fulfil all pending
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
/* FULFILCARD_V1_END */
